import { Link } from 'react-router-dom';

/**
 * Conteúdo da Central de Ajuda — declarativo, versionado com o código.
 *
 * `audience` controla o filtro "Sou usuário / Sou administrador":
 *   - 'user'  → operação do dia a dia (recepção, profissionais)
 *   - 'admin' → dono/gerente (configuração, equipe, assinatura, LGPD)
 *   - 'both'  → aparece nos dois modos
 *
 * A ORDEM do array é a ordem de leitura da página, e ela segue o percurso
 * de quem usa o sistema — não o valor de `audience`. Por isso os
 * separadores abaixo descrevem o tema do grupo (operação, canais com o
 * paciente, administração) e cada grupo pode misturar user/admin/both. O
 * filtro continua sendo feito pelo campo `audience` de cada seção.
 *
 * Regras de escrita (manter!):
 *   - resposta em 2-5 frases, caminho de clique em <strong>
 *   - links internos com <Link> pra navegação direta
 *   - keywords com sinônimos que o usuário digitaria na busca
 */

export type HelpAudience = 'user' | 'admin' | 'both';

export interface HelpItem {
  q: string;
  a: React.ReactNode;
  keywords?: string;
}

export interface HelpSection {
  id: string;
  icon: string;
  title: string;
  audience: HelpAudience;
  items: HelpItem[];
}

export const HELP_SECTIONS: HelpSection[] = [
  // ───────────────────────── COMEÇANDO ─────────────────────────
  {
    id: 'primeiros-passos',
    icon: '🚀',
    title: 'Primeiros passos',
    audience: 'both',
    items: [
      {
        q: 'Por onde começar depois de criar a conta?',
        a: (
          <>
            Complete o cadastro da clínica em <strong>Configurações →
            Informações</strong> (nome, CNPJ, fuso), marque as áreas de
            atuação em <strong>Configurações → Especialidades</strong>,
            cadastre a equipe em <Link to="/equipe">Equipe</Link> e os
            serviços em <Link to="/servicos">Serviços</Link>. Com isso a
            agenda e o faturamento já funcionam.
          </>
        ),
        keywords: 'onboarding setup início configurar comecar',
      },
      {
        q: 'O sistema funciona no celular?',
        a: (
          <>
            Sim — a interface é responsiva e instalável como aplicativo
            (PWA): abra o site no celular e use “Adicionar à tela inicial”.
            Não precisa de App Store.
          </>
        ),
        keywords: 'mobile celular app pwa instalar android iphone',
      },
      {
        q: 'Posso migrar meus dados de outro sistema?',
        a: (
          <>
            Sim. Em <Link to="/pacientes">Pacientes</Link> use
            <strong> Importar</strong> (planilha Excel/CSV com modelo
            fornecido). O modelo cobre nome, CPF, sexo, nascimento (ou{' '}
            <strong>Idade</strong>, que estima o nascimento), celular, e-mail,
            endereço completo, <strong>Estado civil</strong>,{' '}
            <strong>Profissão</strong>, <strong>RG</strong>,{' '}
            <strong>Nível</strong> (Regular/Premium/VIP) e{' '}
            <strong>Observações</strong> (texto livre — histórico, alergias,
            preferências). Antes de importar, um preview mostra os totais
            (novos/duplicados/inválidos). Se precisar de ajuda na migração, abra
            um chamado em <Link to="/suporte">Suporte</Link> — o time acompanha
            a importação com você.
          </>
        ),
        keywords: 'importar migração planilha excel csv outro sistema observações nível tier campos modelo endereço clínica adquirida',
      },
    ],
  },

  // ───────────────────────── OPERAÇÃO DIÁRIA ─────────────────────────
  // Do painel de abertura até a sala de espera: o que a clínica toca
  // todos os dias.
  {
    id: 'painel',
    icon: '📈',
    title: 'Painel e indicadores',
    audience: 'user',
    items: [
      {
        q: 'O que é o Painel que abre quando eu entro no sistema?',
        a: (
          <>
            Ao fazer login você cai direto no <Link to="/painel">Painel</Link>:
            o retrato da clínica no mês corrente (do dia 1 até agora). Ele
            reúne os números de Financeiro, Agenda, Pacientes e Planos de
            tratamento, mais os cards de consultas de hoje, faturas vencidas,
            retenção e os gráficos dos últimos 6 meses. Não há filtro de
            período aqui — os totais do topo são sempre do mês atual.
          </>
        ),
        keywords: 'painel dashboard inicio início home resumo primeira tela indicadores kpi',
      },
      {
        q: 'O que significa cada número do topo?',
        a: (
          <>
            Em <strong>Financeiro</strong>: <strong>Faturado no mês</strong>{' '}
            (faturas emitidas no período, incluindo as já pagas),{' '}
            <strong>Recebido</strong> (as que foram quitadas),{' '}
            <strong>Em aberto</strong> (tudo que ainda não foi pago, sem corte
            de data) e <strong>Vencidas</strong> (quantidade de faturas em
            atraso hoje). Em <strong>Agenda</strong>: consultas do mês,
            realizadas, faltas, canceladas e <strong>Próximas</strong>{' '}
            (agendadas ou confirmadas daqui pra frente, mesmo fora do mês).{' '}
            <strong>Pacientes</strong> traz novos no mês e o total da base;{' '}
            <strong>Planos de tratamento</strong> traz aceitos, concluídos e
            rascunhos.
          </>
        ),
        keywords: 'kpi indicadores faturado recebido em aberto vencidas faltas no-show novos pacientes planos numeros números',
      },
      {
        q: 'Como uso o card “Consultas de hoje”?',
        a: (
          <>
            Ele lista as consultas do dia em ordem de horário (as canceladas
            ficam de fora), com paciente, profissional e status. Quem está{' '}
            <strong>Agendado</strong> ou <strong>Confirmado</strong> ganha o
            botão <strong>Check-in</strong> — um clique registra a chegada sem
            você sair do painel. O card mostra até 8 consultas; em{' '}
            <strong>Ver tudo</strong> você abre a{' '}
            <Link to="/agenda">Agenda</Link> completa.
          </>
        ),
        keywords: 'consultas de hoje check-in checkin chegada recepcao recepção agenda do dia',
      },
      {
        q: 'Pra que serve o card vermelho de faturas vencidas?',
        a: (
          <>
            Ele só aparece quando existem faturas em atraso e mostra as 5 mais
            antigas, com valor e dias de atraso. O botão{' '}
            <strong>Lembrar</strong> dispara na hora um lembrete de cobrança
            por WhatsApp pro paciente — fica desabilitado se ele não tiver
            telefone cadastrado. Em <strong>Ver todas</strong> você abre a
            lista completa em <Link to="/faturas">Faturas</Link>.
          </>
        ),
        keywords: 'faturas vencidas atraso cobranca cobrança lembrete whatsapp inadimplencia inadimplência',
      },
      {
        q: 'O que o bloco de Retenção está me dizendo?',
        a: (
          <>
            Ele conta quantos pacientes diferentes tiveram consulta (não
            cancelada) nos últimos 90, 180 e 365 dias, com o percentual sobre
            a base total. Se houver gente que já se consultou mas não volta há
            mais de 180 dias, aparece um aviso amarelo com a quantidade — são
            os candidatos naturais a uma campanha de reativação.
          </>
        ),
        keywords: 'retencao retenção pacientes ativos dormentes inativos reativacao reativação campanha',
      },
      {
        q: 'Os gráficos do fim da página são de qual período?',
        a: (
          <>
            Os três são dos <strong>últimos 6 meses</strong>:{' '}
            <strong>Receita</strong> (faturado x recebido, mês a mês),{' '}
            <strong>Novos pacientes</strong> por mês e{' '}
            <strong>Top 5 serviços</strong> por quantidade. Passe o mouse sobre
            as barras pra ver os valores exatos. Pra recortes com datas
            escolhidas por você, use <Link to="/relatorios">Relatórios</Link>.
          </>
        ),
        keywords: 'graficos gráficos receita novos pacientes top servicos serviços 6 meses evolucao evolução',
      },
      {
        q: 'Por que o painel do meu colega tem blocos que o meu não tem?',
        a: (
          <>
            O painel é adaptativo: cada bloco só aparece pra quem tem
            permissão de ver aquele dado. Uma recepcionista sem acesso ao
            financeiro, por exemplo, não enxerga os valores de faturamento —
            e nem o card de faturas vencidas. Quem define isso é o
            administrador em <Link to="/equipe">Equipe</Link>.
          </>
        ),
        keywords: 'permissao permissão papel role bloco sumiu nao aparece não aparece financeiro escondido',
      },
    ],
  },
  {
    id: 'agenda',
    icon: '📅',
    title: 'Agenda',
    audience: 'user',
    items: [
      {
        q: 'Como marco uma consulta?',
        a: (
          <>
            Na <Link to="/agenda">Agenda</Link>, clique no horário, escolha
            o paciente (ou cadastre na hora), o profissional e o serviço. O
            sistema bloqueia conflitos de horário do mesmo profissional
            automaticamente e atualiza a tela de todo mundo em tempo real.
          </>
        ),
        keywords: 'agendar marcar consulta horário novo',
      },
      {
        q: 'Apareceu um aviso de "cadastro incompleto/desatualizado" ao agendar. O que é?',
        a: (
          <>
            Ao escolher o paciente, o sistema confere o cadastro e mostra um
            aviso <strong>não-bloqueante</strong> (você sempre consegue salvar a
            agenda). 🟠 <strong>Cadastro incompleto</strong> = falta um dado
            importante (telefone, nascimento) ou há algo inválido (CPF/e-mail).
            🟡 <strong>Cadastro desatualizado</strong> = está tudo preenchido,
            mas ninguém confere há muito tempo. Você tem dois atalhos:{' '}
            <strong>Atualizar cadastro</strong> (abre a ficha do paciente em
            outra aba pra corrigir) e <strong>Está certo</strong> (confirma que
            o cadastro está atual, sem mudar nada). Editar ou confirmar zera o
            aviso. A conferência é por regra simples, não envia dados do
            paciente pra nenhum serviço externo. O dono escolhe quais campos são
            obrigatórios/recomendados e em quantos meses o cadastro conta como
            desatualizado em{' '}
            <strong>Configurações → Atendimento → Regras de cadastro</strong>.
          </>
        ),
        keywords:
          'cadastro incompleto desatualizado aviso paciente agendar falta telefone cpf email atualizar está certo revisar ficha completar dados configurar campos obrigatórios regras',
      },
      {
        q: 'Como funciona consulta recorrente (ex.: sessões semanais)?',
        a: (
          <>
            Ao criar a consulta, marque{' '}
            <strong>Repetir semanalmente / quinzenalmente</strong>, escolha a
            frequência (<strong>semanal</strong> ou <strong>quinzenal</strong>)
            e o número de <strong>ocorrências</strong> (de 2 a 26). A série
            repete sempre no mesmo dia da semana e horário da primeira consulta.
            Cada sessão pode ser remarcada ou cancelada individualmente, sem
            afetar as demais.
          </>
        ),
        keywords: 'recorrência repetir sessões semanal quinzenal série pacote',
      },
      {
        q: 'Paciente faltou ou cancelou — o que eu faço?',
        a: (
          <>
            Abra a consulta e mude o status para <strong>Falta</strong> ou{' '}
            <strong>Cancelada</strong>. A política de cancelamento da
            clínica (multa de no-show, antecedência mínima) é configurada
            pelo administrador e aparece nas confirmações enviadas ao
            paciente.
          </>
        ),
        keywords: 'falta no-show cancelar status remarcar',
      },
      {
        q: 'Como bloqueio horários (almoço, feriado, congresso)?',
        a: (
          <>
            Na <Link to="/agenda">Agenda</Link>, use{' '}
            <strong>Bloquear horário</strong>: informe o período, o{' '}
            <strong>motivo</strong> (obrigatório) e o profissional — ou deixe{' '}
            <strong>Clínica inteira</strong> pra bloquear todo mundo. Feriados e
            folgas da clínica são cadastrados <strong>data a data</strong> em{' '}
            <strong>Configurações → Agenda → Horários</strong>.
          </>
        ),
        keywords: 'bloqueio bloquear folga feriado almoço indisponível motivo',
      },
      {
        q: 'Abriu um buraco na agenda — como acho um horário rápido? (Encaixe)',
        a: (
          <>
            Na <Link to="/agenda">Agenda</Link>, clique em{' '}
            <strong>Encaixar</strong> (ícone de relógio no topo), escolha o
            profissional e o dia: o sistema lista só os{' '}
            <strong>horários realmente livres</strong>, já descontando
            consultas, bloqueios e o expediente daquele profissional. Toque no
            horário e o agendamento abre com a data e a hora prontas. Ele mostra
            as vagas — quem escolher o paciente é você. No rodapé, em{' '}
            <strong>Quem chamar pra preencher</strong>, o sistema ainda sugere
            pacientes com <strong>plano em aberto</strong> ou que{' '}
            <strong>faltaram sem reagendar</strong>, com um atalho de{' '}
            <strong>WhatsApp</strong> — a mensagem é você quem manda.
          </>
        ),
        keywords:
          'encaixe encaixar horario livre vaga buraco agenda rapido disponibilidade quem chamar candidato plano em aberto faltou reagendar whatsapp',
      },
      {
        q: 'O paciente recebe lembrete da consulta?',
        a: (
          <>
            Sim — lembretes automáticos por WhatsApp/e-mail antes da
            consulta (intervalos definidos pelo administrador em{' '}
            <strong>Configurações → Lembretes</strong>). O paciente pode
            confirmar pelo link, e a confirmação aparece na agenda.
            <br />
            <strong>Importante:</strong> WhatsApp só é enviado para quem{' '}
            <strong>autorizou receber mensagens</strong> (opt-in). Sem o aceite,
            nem lembrete nem confirmação saem — por isso o atendimento mostra o
            aviso "Sem opt-in de WhatsApp" quando falta. Registre o aceite na
            ficha do paciente (Consentimentos) ou peça pelo portal.
            <br />
            <br />
            <strong>Já recolheu a autorização de todos no papel?</strong> Em{' '}
            <Link to="/consentimento">Consentimento</Link>, no card de cobertura
            de WhatsApp, o dono tem o botão{' '}
            <strong>"Autorizar todos (coleta presencial)"</strong>, que registra
            o opt-in de toda a base de uma vez. Use só se de fato recolheu a
            autorização de cada paciente — fica tudo na trilha de consentimento.
            <br />
            <br />
            <strong>Como saber se saiu?</strong> No detalhe do agendamento (na
            agenda), em <strong>"Mostrar histórico"</strong>, cada lembrete/
            confirmação aparece com data e hora — e, quando não sai, mostra o
            motivo (ex.: "não enviado: paciente sem opt-in de WhatsApp").
          </>
        ),
        keywords: 'lembrete confirmação whatsapp aviso consulta opt-in optin consentimento nao chega nao recebe autorizar em massa todos coleta presencial papel historico enviado notificacao',
      },
      {
        q: 'Dá para felicitar o paciente no aniversário automaticamente?',
        a: (
          <>
            Sim. Em{' '}
            <strong>Configurações → Comunicação → Lembretes</strong>, ligue{' '}
            <strong>“Aniversário do paciente”</strong>. Todo dia de manhã o
            sistema manda uma mensagem de WhatsApp para quem faz aniversário no
            dia — só para quem <strong>autorizou WhatsApp</strong> (opt-in). Dá
            pra personalizar o texto usando <code>{'{nome}'}</code> para o
            primeiro nome; em branco, usa uma mensagem padrão. Cada paciente
            recebe no máximo uma felicitação por aniversário.
          </>
        ),
        keywords:
          'aniversario parabens felicitacao birthday automatico whatsapp data nascimento paciente comunicacao',
      },
      {
        q: 'Dá para chamar de volta quem não aparece há tempos?',
        a: (
          <>
            Sim. Em{' '}
            <strong>Configurações → Comunicação → Lembretes</strong>, ligue{' '}
            <strong>“Reativação de pacientes inativos”</strong> e escolha a
            janela (ex.: 6 meses). Toda semana o sistema manda um convite de
            retorno por WhatsApp a quem não vem há esse tempo e não tem consulta
            marcada — só para quem <strong>autorizou WhatsApp</strong>, e no
            máximo <strong>um convite por período</strong> (não fica insistindo).
            Personalize o texto com <code>{'{nome}'}</code>; em branco, usa um
            padrão.
          </>
        ),
        keywords:
          'reativacao inativo retorno recall paciente sumido nao vem ha meses convite whatsapp automatico comunicacao dormindo',
      },
      {
        q: 'O paciente recebe uma confirmação na hora que agendo?',
        a: (
          <>
            Sim, se a clínica ligar em{' '}
            <strong>Configurações → Lembretes → Confirmação de agendamento</strong>
            . Ao <strong>criar</strong> o agendamento (não em edições), sai uma
            mensagem de WhatsApp confirmando data/hora — respeitando o opt-in do
            paciente. Dá pra preencher o <strong>endereço da clínica</strong>{' '}
            que aparece na mensagem; em branco, usa o nome da clínica.
          </>
        ),
        keywords: 'confirmacao agendamento whatsapp ao agendar na hora endereco confirmacao_agendamento marcar consulta',
      },
      {
        q: 'O que é o ⚠ com um número no card da consulta?',
        a: (
          <>
            É o <strong>risco de falta</strong> (0 a 100). Ele pesa histórico de
            faltas do paciente, primeira consulta, consulta marcada com muita
            antecedência, faixa de horário que mais falta na sua clínica e se a
            presença já foi confirmada. O selo só aparece em risco{' '}
            <strong>médio (âmbar)</strong> e <strong>alto (vermelho)</strong> —
            passe o mouse pra ver os motivos. O cálculo roda de madrugada, então
            ele reflete a situação do dia.
          </>
        ),
        keywords:
          'risco de falta no-show score badge alerta vermelho amarelo previsao faltar',
      },
      {
        q: 'Como confirmo a presença ou cobro a confirmação em 1 clique?',
        a: (
          <>
            Abra a consulta na <Link to="/agenda">Agenda</Link>:{' '}
            <strong>Confirmar</strong> marca a presença na hora (e derruba o
            risco de falta), sem mandar nada ao paciente. Já{' '}
            <strong>🔔 Reforçar confirmação</strong> reenvia um pedido de
            confirmação por WhatsApp — use nos cards com selo vermelho no dia
            anterior. Se o paciente não tiver telefone ou não tiver autorizado
            WhatsApp, o sistema avisa e não envia.
          </>
        ),
        keywords:
          'confirmar presenca reforcar confirmacao lembrete whatsapp um clique no-show',
      },
    ],
  },
  {
    id: 'funil-vendas',
    icon: '🫙',
    title: 'Funil de vendas (CRM)',
    audience: 'user',
    items: [
      {
        q: 'Para que serve o Funil de vendas?',
        a: (
          <>
            É onde você acompanha <strong>pacientes potenciais</strong> (leads)
            antes de virarem pacientes: quem pediu orçamento, quem veio de uma
            campanha, quem ligou interessado. Em{' '}
            <Link to="/crm">Funil de vendas</Link> cada lead é um cartão que
            caminha pelas etapas até fechar (ou não).
          </>
        ),
        keywords:
          'funil crm lead venda comercial orçamento prospecto potencial oportunidade pipeline',
      },
      {
        q: 'Como adiciono um lead?',
        a: (
          <>
            Em <Link to="/crm">Funil de vendas</Link>, clique{' '}
            <strong>+ Novo lead</strong> (ou o <strong>+ lead</strong> dentro de
            uma coluna). Só o <strong>nome</strong> é obrigatório; telefone,
            origem, valor estimado e observação ajudam a priorizar. Para mover de
            etapa, <strong>arraste o cartão</strong> para outra coluna.
          </>
        ),
        keywords: 'novo lead adicionar cadastrar arrastar mover etapa coluna kanban',
      },
      {
        q: 'Como mudo as etapas do funil?',
        a: (
          <>
            Clique em <strong>Etapas</strong> no topo. Você renomeia, reordena
            (setas), adiciona ou remove etapas, e marca uma como{' '}
            <strong>Ganho</strong> ou <strong>Perda</strong>. Ao remover uma
            etapa que tem leads, eles vão para a primeira etapa — ninguém some.
          </>
        ),
        keywords: 'etapas configurar funil personalizar ganho perda coluna renomear',
      },
      {
        q: 'Follow-up: como não esquecer de retornar um lead?',
        a: (
          <>
            No cartão do lead, preencha <strong>Próximo contato</strong> com a
            data. Quando a data passa, o cartão fica com um aviso{' '}
            <strong>âmbar</strong> e o topo mostra quantos follow-ups estão
            atrasados.
          </>
        ),
        keywords: 'follow-up followup próximo contato retorno atrasado lembrete data',
      },
      {
        q: 'Como falo com o lead no WhatsApp?',
        a: (
          <>
            Se o lead tem telefone, o cartão mostra o atalho{' '}
            <strong>WhatsApp</strong>, que abre a conversa no seu WhatsApp com o
            número já preenchido. É um atalho manual — não envia nada sozinho.
          </>
        ),
        keywords: 'whatsapp lead conversar wa.me atalho mensagem contato',
      },
      {
        q: 'O lead fechou — como viro paciente?',
        a: (
          <>
            No cartão, clique <strong>Converter em paciente</strong>: criamos o
            cadastro do paciente com o nome, telefone e e-mail do lead, e o
            cartão vai para a etapa de <strong>Ganho</strong>. Complete depois o
            CPF e o endereço no perfil (necessários para nota fiscal). O cartão
            passa a mostrar <strong>Ver paciente</strong>.
          </>
        ),
        keywords:
          'converter lead paciente fechar ganho virar cadastro transformar cliente',
      },
    ],
  },
  {
    id: 'pacientes',
    icon: '🧑‍⚕️',
    title: 'Pacientes e portal',
    audience: 'user',
    items: [
      {
        q: 'Como cadastro um paciente?',
        a: (
          <>
            Em <Link to="/pacientes">Pacientes → Cadastrar</Link> (ou direto
            no agendamento). CPF é criptografado e usado só pra evitar
            duplicidade. Campos de endereço são opcionais e ajudam na
            emissão de nota fiscal.
          </>
        ),
        keywords: 'cadastrar paciente novo cpf ficha',
      },
      {
        q: 'O que é o portal do paciente?',
        a: (
          <>
            Um link mágico (sem senha) que você gera no perfil do paciente e
            envia por WhatsApp. Nele o paciente vê histórico de consultas,
            faturas e termos de consentimento — e só o que a clínica liberou
            explicitamente.
          </>
        ),
        keywords: 'portal paciente link mágico acesso histórico',
      },
      {
        q: 'Como colho assinatura de termo de consentimento?',
        a: (
          <>
            Em <Link to="/consentimento">Consentimento</Link> (menu lateral),
            escolha a política/termo e o paciente: ele assina na tela
            (dedo/mouse) e o documento fica arquivado com data e o texto exato
            assinado. Revogações ficam registradas — nada é apagado. O próprio
            paciente também pode <strong>revogar um consentimento pelo portal</strong>{' '}
            (direito LGPD): no portal dele, em "Termos e consentimentos", cada
            item ativo tem o botão <strong>Revogar</strong>. Já a autorização
            para receber <strong>WhatsApp</strong> fica no perfil do paciente,
            aba <strong>Resumo</strong>.
          </>
        ),
        keywords:
          'consentimento termo assinatura digital lgpd autorização opt-in whatsapp revogar portal paciente direito titular',
      },
      {
        q: 'Como anexo exames, radiografias e fotos?',
        a: (
          <>
            No perfil do paciente, aba <strong>Arquivos</strong>: arraste o
            arquivo (imagem, PDF, DICOM ou ZIP de escaneamento, até 50 MB) e
            categorize. Por padrão o paciente NÃO vê o anexo — use o toggle
            “visível ao paciente” quando quiser compartilhar.
          </>
        ),
        keywords: 'anexo upload exame radiografia foto arquivo dicom',
      },
    ],
  },
  {
    id: 'prontuario',
    icon: '📋',
    title: 'Prontuário e fichas clínicas',
    audience: 'user',
    items: [
      {
        q: 'Por que meu prontuário mostra fichas diferentes das de outra clínica?',
        a: (
          <>
            O prontuário se adapta às <strong>especialidades da clínica</strong>:
            odontologia vê odontograma e periodontograma; fisioterapia vê
            mapa corporal, escala de dor e avaliação funcional; estética vê
            ficha de avaliação e fotos antes/depois; nutrição vê
            antropometria. O administrador define as áreas em{' '}
            <strong>Configurações → Especialidades</strong>.
            <br />
            <br />
            <strong>Isso vale também pra lista de fichas.</strong> Em{' '}
            <strong>Nova entrada</strong>, o campo <strong>Tipo de
            registro</strong> só mostra os modelos das áreas marcadas. Clínica
            que nunca configurou nada fica no padrão{' '}
            <strong>odontologia</strong> — e a{' '}
            <strong>Avaliação fisioterapêutica</strong> (CREFITO/COFFITO) e a{' '}
            <strong>Anamnese de depilação a laser / estética</strong>{' '}
            simplesmente não aparecem na lista, sem nenhuma mensagem explicando.
            Se as fichas da sua área sumiram, é aí que se resolve: marque{' '}
            <strong>Fisioterapia</strong>, <strong>Estética</strong> ou{' '}
            <strong>Dermatologia</strong> em Configurações → Especialidades.
            Marcar mais de uma área mostra os dois grupos juntos.
          </>
        ),
        keywords:
          'especialidade odontograma mapa corporal fisioterapia estética módulos ficha sumiu nao aparece tipo de registro crefito depilacao laser anamnese modelo',
      },
      {
        q: 'Desativei uma especialidade — o catálogo daquela área some?',
        a: (
          <>
            Desativar uma especialidade em{' '}
            <strong>Configurações → Especialidades</strong>{' '}
            <strong>esconde a oferta</strong> daquela área, mas{' '}
            <strong>não apaga nada</strong>. No{' '}
            <Link to="/servicos">Catálogo de Serviços</Link> a área some dos
            filtros e o botão de <strong>Novo serviço</strong> deixa de oferecer
            ela — mas os serviços continuam existindo (ligados a agendamentos,
            faturas e planos antigos). Para vê-los, use{' '}
            <strong>Mostrar áreas desativadas</strong> no catálogo; eles
            aparecem com a etiqueta <em>área desativada</em>. Religou a
            especialidade? Tudo volta a aparecer, do jeito que estava. Áreas
            genéricas (Higiene, Cirurgia) nunca são escondidas.
          </>
        ),
        keywords:
          'especialidade desativar catálogo serviços some sumiu esconder oferta odontologia dados histórico reversível religar área desativada mostrar',
      },
      {
        q: 'O que mostra a aba "Resumo clínico"?',
        a: (
          <>
            Um panorama do paciente em quatro números —{' '}
            <strong>consultas realizadas</strong>, <strong>registros
            ativos</strong>, <strong>última visita</strong> e{' '}
            <strong>próxima</strong> — mais os{' '}
            <strong>diagnósticos e queixas ativas</strong> em etiquetas. É a
            primeira tela do prontuário, pra você se situar em 5 segundos antes
            de chamar o paciente. Esses números saem direto dos registros que a
            equipe lançou (os diagnósticos vêm de Evolução → Diagnóstico) — não
            são gerados por IA. Logo abaixo deles fica o card{' '}
            <strong>Resumo do prontuário por IA</strong>, esse sim opcional e
            gerado sob demanda.
          </>
        ),
        keywords:
          'resumo clinico panorama kpi ultima visita diagnosticos queixas ativas prontuario',
      },
      {
        q: 'Posso pedir um resumo do prontuário pra IA?',
        a: (
          <>
            Pode. No prontuário, seção <strong>📊 Resumo clínico</strong>,
            clique em <strong>Resumir com IA</strong>. Ele lê o histórico e
            devolve um parágrafo com o panorama do paciente mais três listas:{' '}
            <strong>⚠️ Alertas de segurança</strong> (alergias, anticoagulantes,
            o que não pode passar batido), <strong>condições em
            acompanhamento</strong> e <strong>medicações em uso</strong>. Tem um
            link <strong>Copiar</strong> pra levar o texto pra outro lugar.
            <br />
            <br />
            Três coisas pra saber antes de confiar. O resumo{' '}
            <strong>não fica salvo</strong> — sai da tela quando você troca de
            paciente, e gerar de novo pode dar um texto diferente. Prontuário
            muito longo é <strong>cortado sem avisar</strong>, então o resumo
            pode não cobrir os registros mais antigos. E ele é apoio de leitura,
            não fonte: <strong>confirme na fonte</strong> antes de decidir
            conduta. O botão exige plano <strong>pro</strong> — sem ele aparece
            um cadeado <strong>🔒 Plano pro</strong> no lugar.
          </>
        ),
        keywords:
          'resumo ia resumir prontuario historico panorama alertas alergias medicacoes condicoes plano pro copiar',
      },
      {
        q: 'O que é a coluna da direita no prontuário?',
        a: (
          <>
            É o <strong>contexto do paciente</strong>: próxima consulta
            marcada, resumo do plano de tratamento (com link “Abrir plano
            completo”) e o financeiro — <strong>saldo devedor</strong> e{' '}
            <strong>faturas em aberto</strong>. Serve pra você não trocar de tela
            no meio do atendimento. Ela aparece em telas largas e se esconde
            sozinha nas seções que precisam de espaço (Odontograma, Avaliações e
            Imagens & fotos).
          </>
        ),
        keywords:
          'coluna contexto lateral direita prontuario proxima consulta plano saldo devedor faturas',
      },
      {
        q: 'Como registro a evolução de uma sessão?',
        a: (
          <>
            Na aba <strong>Prontuário</strong> do paciente, use{' '}
            <strong>+ Novo registro clínico</strong> (anamnese, evolução
            SOAP, diagnóstico, prescrição ou nota). O prontuário é
            <em> imutável</em>: corrigir cria uma nova versão e a anterior
            fica no histórico — exigência de rastreabilidade clínica.
          </>
        ),
        keywords: 'evolução soap registro anamnese nota sessão imutável',
      },
      {
        q: 'Errei um registro clínico — como corrijo?',
        a: (
          <>
            No registro, clique em <strong>Editar</strong>. Como o prontuário é
            imutável, editar não apaga nada: cria uma <strong>nova versão</strong>{' '}
            do registro e a anterior fica guardada no histórico (rastreabilidade
            clínica e LGPD). Dá pra editar <strong>anamnese, avaliação, evolução
            SOAP, plano de tratamento e notas em texto livre</strong> — o form
            abre com os campos preenchidos pra você ajustar. Já{' '}
            <strong>receita, atestado/encaminhamento e análise de IA</strong> têm
            documento próprio e não se editam campo a campo: use{' '}
            <strong>Revogar</strong> (informando o motivo) e crie um novo.
          </>
        ),
        keywords:
          'editar corrigir erro registro prontuario prontuário nova versao versão revogar historico histórico ajustar receita atestado imutavel imutável supersede',
      },
      {
        q: 'Como atendo uma consulta direto da agenda?',
        a: (
          <>
            Abra a consulta na <strong>Agenda</strong> e clique em{' '}
            <strong>🩺 Atender agora — abrir prontuário</strong>: a ficha do
            paciente abre na aba Prontuário com a evolução já iniciada e{' '}
            <strong>vinculada àquela consulta</strong> (o registro ganha a
            etiqueta 📅 com data e hora). O botão aparece só para perfis
            clínicos — recepção gerencia status da agenda sem acessar o
            conteúdo do prontuário.
          </>
        ),
        keywords: 'atender agora agenda consulta evolução vinculada atendimento',
      },
      {
        q: 'O que é a barra "Visão" no topo do prontuário?',
        a: (
          <>
            Em clínicas com 2+ especialidades, a barra{' '}
            <strong>Visão</strong> filtra o prontuário pela sua área:
            fisioterapeuta fixa a visão Fisioterapia e deixa de rolar por
            odontograma; dentista, o inverso. A escolha fica salva — e se o
            seu registro profissional (CRO, CREFITO…) está no perfil, a
            visão certa já vem selecionada. Blocos do paciente (alertas,
            plano, histórico médico, receitas) aparecem em todas as visões.
          </>
        ),
        keywords: 'visão lente especialidade filtro multi prontuário odontologia fisioterapia',
      },
      {
        q: 'Como emito receita, atestado ou encaminhamento?',
        a: (
          <>
            Na ficha do paciente, seção{' '}
            <strong>💊 Receitas e documentos</strong>: "+ Nova receita"
            (simples ou controle especial em 2 vias) e "+ Atestado /
            encaminhamento" (comparecimento, afastamento, encaminhamento).
            Tudo vira registro permanente no prontuário e abre pronto pra
            imprimir com a <strong>sua assinatura</strong> (cadastre a
            imagem em Perfil → Assinatura para documentos). No atestado de
            afastamento, o CID só sai com a autorização expressa do
            paciente marcada (LGPD).
          </>
        ),
        keywords:
          'receita atestado encaminhamento documento imprimir assinatura cid comparecimento afastamento',
      },
      {
        q: 'Posso ditar a evolução por áudio? (Áudio → SOAP)',
        a: (
          <>
            Sim: no template <strong>Evolução SOAP</strong>, clique em{' '}
            <strong>▶ Iniciar gravação</strong>, narre a consulta e{' '}
            <strong>■ Parar</strong> — a IA transcreve e separa em
            S/O/A/P pra você revisar antes de salvar. Dicas pra acertar de
            primeira: fale perto do microfone, em frases completas
            ("paciente relata…, ao exame…, hipótese…, plano…"); a{' '}
            <strong>transcrição aparece aberta</strong> no painel pra você
            conferir o que a IA ouviu. Gravou por engano?{' '}
            <strong>✕ Cancelar</strong> descarta tudo — nada é enviado.
            Requer a integração OpenAI ativa (admin) e consentimento do
            paciente pra gravação.
          </>
        ),
        keywords:
          'áudio ditado gravação transcrição ia soap whisper cancelar microfone',
      },
      {
        q: 'Como uso o odontograma?',
        a: (
          <>
            Clique no dente (vista lateral) pra marcar o estado geral, ou
            numa face específica do círculo (V/L/M/D/O). Salve versões em{' '}
            <strong>Versões do odontograma</strong> pra comparar a evolução
            entre consultas. O histórico por dente mostra toda a cronologia.
          </>
        ),
        keywords: 'odontograma dente face fdi periodontograma psr',
      },
      {
        q: 'Como marco pontos de dor no mapa corporal (fisioterapia)?',
        a: (
          <>
            No card <strong>Mapa corporal</strong>, escolha frente/costas,
            ajuste a intensidade EVA (0–10) e clique na silhueta. Cada
            “Salvar avaliação” cria um registro datado — na próxima sessão
            você compara a evolução pelo histórico.
          </>
        ),
        keywords: 'mapa corporal dor eva fisioterapia silhueta avaliação',
      },
      {
        q: 'Como uso a seção "Imagens & fotos" (antes/depois)?',
        a: (
          <>
            Ela reúne a comparação <strong>antes / depois</strong>: escolha uma
            foto em cada lado e o sistema mostra as duas lado a lado com as
            datas. As imagens vêm da aba <strong>Arquivos</strong> do paciente
            (categoria “Foto clínica”) — o envio é feito por lá, até 50 MB por
            arquivo. Nessa mesma seção fica o botão{' '}
            <strong>🔬 Analisar pele com IA</strong>, que exige marcar o
            consentimento do paciente antes de enviar a imagem.
          </>
        ),
        keywords:
          'imagens fotos antes depois comparar estética upload arquivos foto clinica analisar pele ia',
      },
      {
        q: 'Como exporto o prontuário em PDF?',
        a: (
          <>
            Na aba Prontuário, <strong>Exportar prontuário (PDF)</strong> —
            gera o documento completo com identificação, registros e
            assinaturas, pronto pra atender pedido do paciente ou
            encaminhamento.
          </>
        ),
        keywords: 'exportar pdf imprimir prontuário encaminhamento',
      },
      {
        q: 'Dá pra usar o prontuário no tablet, em pé (retrato)?',
        a: (
          <>
            Sim — foi feito pra isso. Em telas menores que um notebook, o menu
            de seções (Resumo clínico, Evolução, Odontograma…) vira uma{' '}
            <strong>barra horizontal deslizante no topo</strong> e o conteúdo
            clínico ocupa a largura inteira; a coluna de contexto some pra não
            espremer. Em telas largas ele volta a ser a coluna lateral de
            sempre. Não precisa configurar nada: é só girar o tablet.
          </>
        ),
        keywords:
          'tablet ipad retrato vertical celular responsivo prontuario cadeira layout',
      },
    ],
  },
  {
    id: 'fichas-avaliacao',
    icon: '🩺',
    title: 'Fichas de avaliação e planejamento',
    audience: 'user',
    items: [
      {
        q: 'Onde ficam as fichas de avaliação do paciente?',
        a: (
          <>
            Abra o paciente em <Link to="/pacientes">Pacientes</Link> (ou em{' '}
            <Link to="/prontuario">Prontuário</Link>) e clique na aba{' '}
            <strong>✨ Avaliações</strong>. Ela só aparece quando as áreas da
            clínica habilitam alguma ficha — o administrador marca isso em{' '}
            <strong>Configurações → Especialidades</strong>; em clínica com
            mais de uma área, a barra <strong>Visão</strong> no topo filtra as
            fichas pela sua especialidade. Toda ficha salva vira um registro
            datado no prontuário daquele paciente: salvar a próxima nunca
            apaga a anterior.
          </>
        ),
        keywords:
          'fichas avaliação avaliacoes aba onde encontrar especialidade lente visão visao módulo modulo',
      },
      {
        q: 'Como marco a dor no mapa corporal (fisioterapia)?',
        a: (
          <>
            No card <strong>Mapa corporal</strong>, escolha{' '}
            <strong>Frente</strong> ou <strong>Costas</strong>, ajuste a
            intensidade EVA (0 a 10) e clique na silhueta — a cor do ponto vai
            do verde ao vermelho conforme a intensidade. Clique em cima de um
            ponto para removê-lo. Cada <strong>Salvar avaliação</strong> cria
            um registro datado, e o histórico do card mostra a data, o lado e
            o EVA máximo de cada sessão.
          </>
        ),
        keywords:
          'mapa corporal dor eva fisioterapia silhueta frente costas ponto intensidade',
      },
      {
        q: 'Para que serve a Escala de dor (EVA)?',
        a: (
          <>
            É o registro rápido da dor do dia: arraste a régua de 0 a 10,
            escreva o contexto se quiser (ex.: “lombar, ao sentar”) e clique em{' '}
            <strong>Registrar</strong>. As últimas medições viram barrinhas no
            próprio card, da mais antiga à mais recente — dá pra mostrar a
            evolução ao paciente em segundos, e cada medição fica datada no
            prontuário.
          </>
        ),
        keywords:
          'escala de dor eva 0 a 10 evolução evolucao grafico gráfico fisioterapia registrar',
      },
      {
        q: 'O que é o Planejador de injetáveis?',
        a: (
          <>
            É a ficha de estética para planejar e registrar a aplicação de
            injetáveis. Ela tem três modos, nos botões do topo:{' '}
            <strong>360º anatômico</strong> (o padrão),{' '}
            <strong>Ilustração (3 vistas)</strong> — frontal, esquerda e
            direita, com rosto feminino ou masculino — e{' '}
            <strong>Foto do paciente</strong>. Os pontos valem para a sessão
            inteira: trocar de ângulo ou de vista não apaga nada.
          </>
        ),
        keywords:
          'injetáveis injetaveis planejador toxina botox preenchimento harmonização harmonizacao facial hof estética estetica',
      },
      {
        q: 'Como marco os pontos no tablet, sem teclado?',
        a: (
          <>
            Toque num chip de <strong>Produto</strong>, escolha a{' '}
            <strong>Dose</strong> (chips prontos ou os botões − e +, de meio em
            meio) e, se quiser, a <strong>Região</strong> e o{' '}
            <strong>Lote</strong> — depois é só tocar no rosto para cravar o
            ponto com essas informações. O primeiro produto já vem
            selecionado, então o primeiro toque não dá erro. Arrastar na
            horizontal gira o rosto em vez de marcar. Num ponto já criado:{' '}
            <strong>toque para editar</strong> (troca produto, dose, região e
            lote, ou exclui) ou <strong>arraste para reposicionar</strong>;{' '}
            <strong>↩ Desfazer</strong>, ao lado do contador, remove o último
            ponto. A lista de produtos vem do seu{' '}
            <Link to="/estoque">Estoque</Link> somada aos padrões, e{' '}
            <strong>+ Novo</strong> guarda um produto extra naquele aparelho.
            Tudo funciona com o dedo ou com a <strong>caneta (stylus)</strong> no
            tablet: marcar, arrastar, girar o rosto e editar são por toque, e os
            chips de produto, dose e região evitam o teclado. Só digitar um
            produto novo ou um lote abre o teclado virtual.
          </>
        ),
        keywords:
          'tablet toque caneta stylus pointer marcar ponto produto dose unidades ml lote região regiao chips sem teclado editar corrigir alterar desfazer excluir remover reposicionar',
      },
      {
        q: 'No retorno, dá para repetir o mapa da última sessão?',
        a: (
          <>
            Dá. Quando o paciente já tem uma sessão de injetáveis, aparece o
            botão <strong>⟳ Repetir última sessão</strong> ao lado do contador:
            ele carrega todos os pontos da última aplicação (em todos os
            ângulos) para a sessão atual, para você só ajustar o que mudou.{' '}
            <strong>Lote e validade não são copiados</strong> de propósito — cada
            sessão usa frascos novos, então você registra o lote atual e a
            rastreabilidade sanitária recomeça do zero. Se já houver pontos
            marcados, o sistema pede confirmação antes de substituir.
          </>
        ),
        keywords:
          'repetir última sessão ultima retorno clonar copiar mapa injetáveis injetaveis pontos lote validade frasco novo rastreabilidade ajustar reaplicação reaplicacao',
      },
      {
        q: 'Por que aparece "⚠ N sem lote" ao salvar?',
        a: (
          <>
            É um lembrete de rastreabilidade sanitária. Ao lado do botão{' '}
            <strong>Salvar sessão</strong>, o sistema mostra quantos pontos ainda
            estão sem o <strong>lote</strong> do produto; se você salvar assim,
            ele pede uma confirmação. Não é um bloqueio — em uma urgência dá para
            gravar sem o lote — mas registrar o lote é o que permite rastrear os
            pacientes atendidos caso haja um recall do fabricante (ANVISA). Para
            preencher, toque no ponto e informe o <strong>Lote</strong> (e a
            validade, se quiser) antes de salvar.
          </>
        ),
        keywords:
          'lote sem lote aviso rastreabilidade sanitária sanitaria anvisa recall validade frasco produto injetáveis injetaveis salvar confirmação confirmacao',
      },
      {
        q: 'Como funciona o modo 360º?',
        a: (
          <>
            Arraste o rosto na horizontal (ou use ‹ ›) e ele gira de forma
            contínua por oito ângulos, do frontal ao posterior — a etiqueta
            embaixo mostra em qual você está. Cada ponto fica ancorado no
            ângulo em que foi marcado: some quando você gira para o outro lado
            e reaparece quando volta, como acontece ao olhar o paciente de
            perto. <strong>Editar</strong> e <strong>↩ Desfazer</strong> valem
            aqui também: toque num ponto para ajustar produto, dose ou região.
          </>
        ),
        keywords:
          '360 girar rodar rosto ângulo angulo perfil arrastar turntable anatômico anatomico editar desfazer ponto',
      },
      {
        q: 'Posso planejar sobre a foto do próprio paciente?',
        a: (
          <>
            Sim. No modo <strong>Foto do paciente</strong>, toque em{' '}
            <strong>Adicionar foto do paciente</strong> para tirar na hora ou
            escolher do aparelho — a imagem é reduzida automaticamente e os
            pontos são cravados sobre ela. Tocar num ponto já marcado o abre
            para <strong>editar</strong>, e <strong>↩ Desfazer</strong> remove o
            último. Essa foto é guardada junto com a sessão no prontuário, então
            é dado sensível: registre o consentimento do paciente. Para salvar
            nesse modo é preciso ter a foto e ao menos um ponto.
          </>
        ),
        keywords:
          'foto do paciente câmera camera imagem real sobre a foto lgpd consentimento trocar foto',
      },
      {
        q: 'Como mostro o plano para o paciente?',
        a: (
          <>
            Com os pontos marcados, clique em <strong>Apresentar</strong>: abre
            uma tela cheia só de leitura, com o rosto grande, os pontos e o
            resumo por produto — sem controles de edição, para ninguém mexer
            sem querer (Esc fecha). O botão <strong>⧉ Janela</strong> abre a
            mesma apresentação numa janela separada, no endereço{' '}
            <strong>/apresentar/injetaveis</strong>, para você jogar na TV ou
            no segundo monitor enquanto continua trabalhando na sua tela.
          </>
        ),
        keywords:
          'apresentar apresentação apresentacao tela cheia segunda tela monitor tv projetor janela mostrar ao paciente',
      },
      {
        q: 'O que faz o botão “▶ Apresentar plano”?',
        a: (
          <>
            Ele conduz uma explicação guiada: o rosto gira sozinho e os pontos
            são revelados um a um, com um cartão mostrando o produto, a região,
            o tipo de efeito esperado e a duração estimada daquela família de
            produto. Dá pra pausar, avançar e voltar (também pelas setas do
            teclado). A opção <strong>✨ Prévia (ilustrativo)</strong> acende as
            áreas tratadas apenas para indicar onde o efeito é esperado — a
            própria tela avisa que isso não representa garantia de resultado.
          </>
        ),
        keywords:
          'tour guiado apresentar plano explicação explicacao ponto a ponto prévia previa ilustrativo pausar',
      },
      {
        q: 'Como gero o termo de consentimento da sessão?',
        a: (
          <>
            Clique em <strong>Gerar termo</strong>: o texto já vem montado com
            a data, os produtos, o total por produto, as regiões e os lotes
            daquela sessão, e você pode editá-lo antes de confirmar. O paciente
            assina no painel com o dedo ou a caneta — deixar em branco significa
            que a assinatura foi feita no papel. Ao clicar em{' '}
            <strong>Registrar termo</strong>, ele é arquivado na aba{' '}
            <strong>Consentimentos</strong> do prontuário do paciente.
          </>
        ),
        keywords:
          'termo de consentimento assinar assinatura canvas injetáveis injetaveis gerar termo registrar',
      },
      {
        q: 'A sessão dá baixa no estoque sozinha?',
        a: (
          <>
            Sim. Ao <strong>Salvar sessão</strong>, o sistema soma as doses por
            produto (e por lote, quando informado) e lança a saída no{' '}
            <Link to="/estoque">Estoque</Link> com o motivo “uso”, vinculada
            àquela sessão. O encontro é feito pelo{' '}
            <strong>nome do produto</strong>: só há baixa no que estiver
            cadastrado e ativo no estoque com o mesmo nome — por isso vale
            escolher os chips que já vêm da sua lista de produtos. Se não
            encontrar, a sessão é salva do mesmo jeito e aquele item apenas não
            é baixado.
          </>
        ),
        keywords:
          'estoque baixa automática automatica consumo saída saida lote produto injetáveis injetaveis movimento',
      },
      {
        q: 'Como comparo fotos antes e depois?',
        a: (
          <>
            No card <strong>Fotos antes / depois</strong>, escolha uma imagem
            em cada lado e elas aparecem lado a lado com as datas. As fotos vêm
            dos anexos do paciente — suba antes pela aba{' '}
            <strong>Arquivos</strong> (categoria “Foto clínica”). Enquanto o
            paciente não tiver nenhuma imagem anexada, o card avisa que não há
            fotos.
          </>
        ),
        keywords:
          'antes e depois comparar fotos evolução evolucao estética estetica dermato anexos arquivos',
      },
    ],
  },
  {
    id: 'planos-pacotes',
    icon: '📦',
    title: 'Planos e pacotes de sessões',
    audience: 'user',
    items: [
      {
        q: 'Como vendo um pacote (ex.: 10 sessões de laser)?',
        a: (
          <>
            Adicione <strong>um único item</strong> no plano com{' '}
            <strong>Qtd = 10</strong> — não crie 10 itens separados. É a
            quantidade maior que 1 que transforma o item em pacote. Ao
            registrar o <strong>aceite</strong> do plano, cada unidade vira uma
            sessão numerada e a linha do item passa a mostrar{' '}
            <strong>"0 de 10 feitas · 10 restantes"</strong>, com um chip por
            sessão.
          </>
        ),
        keywords:
          'pacote sessões vender criar quantidade qtd laser 10 sessoes multiplas',
      },
      {
        q: 'Onde vejo quantas sessões o paciente já fez?',
        a: (
          <>
            Em três lugares, sempre com o mesmo número{' '}
            <strong>"X de N feitas · Y restantes"</strong>: na{' '}
            <strong>ficha do paciente</strong> (aba Resumo, card{' '}
            <em>Pacotes e sessões</em>, com barra de progresso), no{' '}
            <strong>prontuário</strong> (card do plano) e ao abrir o{' '}
            <strong>atendimento na agenda</strong> (aviso do pacote). O controle
            sessão a sessão fica na página do plano.
          </>
        ),
        keywords:
          'onde ver sessões restantes saldo pacote ficha paciente prontuario agenda quantas faltam fez',
      },
      {
        q: 'Como dou baixa nas sessões?',
        a: (
          <>
            Na página do plano, o dia a dia é um botão só:{' '}
            <strong>✓ Marcar sessão feita</strong> — ele baixa a próxima sessão
            aberta. Precisa de algo específico (vincular a um agendamento, pular
            ou debitar falta)? Clique em <strong>⋯ mais</strong> para abrir o
            controle sessão a sessão.
            <br />
            <strong>Com agendamento:</strong> em ⋯ mais, use{' '}
            <strong>Vincular</strong> e escolha o agendamento — a baixa acontece{' '}
            <em>sozinha</em> no check-out, sem abrir o plano de novo.
          </>
        ),
        keywords:
          'consumir baixa sessão marcar feita vincular agendamento check-out checkout encaixe mais',
      },
      {
        q: 'Paciente faltou sem avisar — cobro a sessão ou não?',
        a: (
          <>
            Na página do plano, abra <strong>⋯ mais</strong> na sessão.{' '}
            <strong>Debitar falta</strong> tira a sessão do pacote sem marcá-la
            como realizada — é o que o contrato assinado autoriza quando não
            houve aviso prévio. Use <strong>Pular</strong> quando a ausência foi
            justificada (atestado, aviso no prazo): aí a sessão volta pro saldo e
            pode ser reagendada. As duas pedem motivo, e o motivo é a sua defesa
            se o paciente contestar depois.
          </>
        ),
        keywords:
          'falta faltou no-show noshow nao compareceu não compareceu debitar cobrar sessao perdida pular justificada atestado mais',
      },
      {
        q: 'Vou fazer um retoque de cortesia — como registro sem descontar do pacote?',
        a: (
          <>
            Retoque entra como sessão <strong>avulsa</strong>, fora da
            contagem: ela aparece com o símbolo ↺ e não mexe no "X/N sessões".
            É o que o contrato de estética prevê quando o retoque acontece
            dentro do prazo informado ao paciente. Fora do prazo, é
            procedimento novo — orce à parte.
          </>
        ),
        keywords: 'retoque cortesia complementacao complementação gratis grátis nao desconta não desconta pacote estetica estética',
      },
      {
        q: 'O pacote tem prazo de validade?',
        a: (
          <>
            Tem, quando o contrato define. A contagem começa na{' '}
            <strong>primeira sessão consumida</strong>, não na assinatura.
            Depois do vencimento o sistema recusa consumir e pede uma
            justificativa — se a clínica decidir liberar mesmo assim, a decisão
            fica registrada em nome de quem liberou.
          </>
        ),
        keywords: 'validade prazo vencimento pacote 180 dias expirou venceu liberar excecao exceção',
      },
      {
        q: 'Marquei a sessão errada — como corrijo?',
        a: (
          <>
            No chip da sessão, clique em <strong>Desfazer</strong> — ela
            volta a Pendente. Funciona também para desfazer um{' '}
            <strong>Debitar falta</strong> registrado por engano ou contestado
            com razão. Toda alteração fica no histórico de movimentações do
            plano, com quem fez e quando.
          </>
        ),
        keywords: 'desfazer pular sessão errada corrigir auditoria',
      },
      {
        q: 'Por que não consigo consumir sessões?',
        a: (
          <>
            Sessões só existem em planos <strong>Aceitos</strong> ou{' '}
            <strong>Em andamento</strong> — em rascunho/proposta ainda não
            foram geradas. Consumir uma sessão já realizada também é
            bloqueado; use Desfazer primeiro se precisar corrigir.
          </>
        ),
        keywords: 'sessão bloqueada aceite rascunho proposta erro',
      },
    ],
  },
  {
    id: 'contratos',
    icon: '📑',
    title: 'Contratos do paciente',
    audience: 'both',
    items: [
      {
        q: 'Onde emito um contrato para o paciente?',
        a: (
          <>
            No <Link to="/prontuario">Prontuário</Link> do paciente, aba{' '}
            <strong>Contratos</strong>, clique em{' '}
            <strong>+ Emitir contrato</strong>. Escolha o modelo, vincule o
            plano de tratamento (opcional — preenche o Anexo I sozinho) e
            informe a forma de pagamento. O sistema puxa os dados do cadastro:
            você não redigita nada.
          </>
        ),
        keywords:
          'contrato emitir gerar novo paciente prestacao prestação de servicos serviços modelo',
      },
      {
        q: 'O sistema não deixou emitir e falou de conselho — por quê?',
        a: (
          <>
            O modelo exige responsável técnico inscrito num conselho
            específico (CREFITO para fisioterapia, CRO para odontologia e
            estética), e nenhum profissional da equipe tem esse registro
            preenchido. Cadastre em <Link to="/equipe">Equipe</Link> →{' '}
            <strong>Registro profissional</strong>, com tipo de conselho,
            número e UF. É uma trava de propósito: ofertar o serviço sem
            RT habilitado é irregularidade perante o conselho.
          </>
        ),
        keywords:
          'nao consigo emitir não consigo bloqueado conselho crefito cro registro profissional responsavel tecnico responsável técnico rt erro',
      },
      {
        q: 'Apareceu uma lista de campos faltando — e agora?',
        a: (
          <>
            O contrato só sai completo. Se faltar CPF do paciente, CNPJ da
            clínica ou os dados de quem assina pela empresa, o sistema lista{' '}
            <strong>tudo</strong> o que falta de uma vez, em vez de um erro por
            vez. Razão social, representante e foro ficam em{' '}
            <Link to="/configuracoes">Configurações</Link> →{' '}
            <strong>Contratos</strong> e são preenchidos uma única vez.
          </>
        ),
        keywords:
          'campos faltando obrigatorio obrigatório cpf cnpj razao social razão representante foro configuracao configuração',
      },
      {
        q: 'Pediu para vincular um plano de tratamento — por quê?',
        a: (
          <>
            Porque esse modelo descreve os valores e as sessões no{' '}
            <strong>Anexo I</strong>, e eles saem do plano — ninguém redigita.
            Monte o plano do paciente em{' '}
            <Link to="/planos">Planos de Tratamento</Link> e escolha-o no campo{' '}
            <strong>Plano de tratamento</strong> ao emitir. A validade do
            pacote também vem daí.
          </>
        ),
        keywords:
          'plano de tratamento vincular anexo i valores sessoes sessões obrigatorio obrigatório emitir contrato',
      },
      {
        q: 'Como o paciente assina?',
        a: (
          <>
            No cartão do contrato, <strong>Registrar assinatura</strong>. Ele
            assina na tela (dedo no celular ou mouse) ou você marca{' '}
            <strong>Assinado em papel</strong> se imprimiu. A assinatura na
            tela vale como aceite eletrônico entre as partes — não é
            assinatura ICP-Brasil, e a tela diz isso ao paciente.
          </>
        ),
        keywords:
          'assinar assinatura paciente tela dedo papel eletronica eletrônica icp brasil aceite',
      },
      {
        q: 'Preciso corrigir um contrato já assinado. Como edito?',
        a: (
          <>
            Não edita — e isso é proposital. O texto assinado fica congelado
            com um código de verificação no rodapé; se desse para alterar
            depois, o documento não provaria nada. Para corrigir,{' '}
            <strong>Cancele</strong> informando o motivo e emita um novo.
          </>
        ),
        keywords:
          'editar corrigir alterar contrato assinado cancelar refazer imutavel imutável hash verificacao verificação',
      },
      {
        q: 'Onde vejo e ativo os modelos de contrato?',
        a: (
          <>
            Em <Link to="/configuracoes">Configurações</Link> →{' '}
            <strong>Contratos</strong> você vê cada modelo, qual conselho ele
            exige do responsável técnico e a validade padrão do pacote — e
            liga ou desliga o que aparece na hora de emitir. Nesta versão o{' '}
            <strong>texto</strong> dos modelos é instalado pela equipe do
            DentalFlow; para revisar cláusula, fale com o suporte.
          </>
        ),
        keywords:
          'modelo template ativar desativar texto clausula cláusula versao versão conselho validade configuracoes configurações',
      },
      {
        q: 'Posso usar os modelos que vieram no sistema como estão?',
        a: (
          <>
            Eles são um <strong>ponto de partida</strong>, escritos para o
            caso comum de clínica odontológica e de estética no Brasil — não
            uma peça jurídica pronta para a sua realidade. Leve à revisão de
            um advogado de direito da saúde antes de usar com paciente,
            principalmente as cláusulas de rescisão, multa e limite de atuação
            profissional.
          </>
        ),
        keywords:
          'modelo pronto usar advogado revisao revisão juridico jurídico responsabilidade risco clausula cláusula',
      },
    ],
  },
  {
    id: 'estoque',
    icon: '📦',
    title: 'Estoque e compras',
    audience: 'user',
    items: [
      {
        q: 'Como cadastro um produto no estoque?',
        a: (
          <>
            Em <Link to="/estoque">Estoque</Link>, aba <strong>Produtos</strong>,
            clique em <strong>Novo produto</strong> e informe nome e unidade
            (un, ml, frasco, caixa, ampola ou seringa). Os campos opcionais
            fazem o sistema trabalhar por você: <strong>estoque mínimo</strong>{' '}
            e <strong>ponto de reposição</strong> alimentam os alertas e a
            sugestão de compra, <strong>estoque máximo</strong> define até
            quanto repor, e <strong>custo unitário</strong> é o que gera o
            valor em estoque. Categoria, fornecedor padrão e código de
            barras/SKU completam a ficha.
          </>
        ),
        keywords: 'cadastrar produto novo item unidade categoria minimo mínimo maximo máximo ponto de reposicao reposição fornecedor custo sku',
      },
      {
        q: 'Como fazer o estoque baixar sozinho quando um serviço é realizado?',
        a: (
          <>
            Monte a <strong>ficha técnica</strong> do serviço: em{' '}
            <Link to="/servicos">Serviços</Link>, edite o serviço e abra{' '}
            <strong>📦 Ficha técnica</strong>. Liste os produtos e a quantidade
            que aquele serviço consome (ex.: 1 anestésico, 2 luvas). Quando um
            atendimento desse serviço é marcado como{' '}
            <strong>concluído</strong>, o sistema debita esses produtos do
            estoque automaticamente, com o motivo “uso”. É a mesma mecânica dos
            injetáveis, agora para qualquer serviço. A baixa não se repete se o
            atendimento for concluído de novo, e só acontece se houver saldo
            cadastrado. Editar a ficha exige permissão de gerenciar estoque.
          </>
        ),
        keywords: 'ficha tecnica técnica servico serviço baixa automática automatica consumo insumo produto ao concluir atendimento receita bill of materials',
      },
      {
        q: 'Como registro entrada e saída de material?',
        a: (
          <>
            Na linha do produto use <strong>+ Entrada</strong> ou{' '}
            <strong>− Saída</strong> (também dá pra abrir o produto e usar os
            botões de dentro). Informe a quantidade e o motivo — compra, uso,
            perda, ajuste, vencimento ou devolução. Na entrada você ainda pode
            gravar lote, validade, fornecedor e custo unitário; esse custo vira
            o novo custo de referência do produto. Nada é apagado: cada
            movimentação fica no histórico com data, autor e saldo resultante.
          </>
        ),
        keywords: 'entrada saida saída movimentacao movimentação baixa consumo perda ajuste historico histórico',
      },
      {
        q: 'Como funciona lote, validade e a ordem de uso (FEFO)?',
        a: (
          <>
            Ao dar entrada, preencha <strong>lote</strong> e{' '}
            <strong>validade</strong>. Abrindo o produto você vê a lista de{' '}
            <strong>Lotes em estoque</strong> ordenada pela validade mais
            próxima, com a etiqueta <strong>usar 1º</strong> no primeiro. Na
            saída, o sistema já sugere esse lote — é o FEFO (o que vence antes
            sai antes), evitando material perdido na prateleira.
          </>
        ),
        keywords: 'lote validade vencimento fefo peps rastreabilidade qual usar primeiro',
      },
      {
        q: 'Posso usar leitor de código de barras?',
        a: (
          <>
            Sim. Na aba <strong>Produtos</strong> o campo{' '}
            <strong>Bipar código</strong> já vem selecionado: passe o leitor a
            laser (ele digita o código e dá Enter) e o produto abre direto na
            tela de <strong>Entrada</strong>. Se o código não estiver cadastrado
            em nenhum produto, o sistema abre o cadastro de produto novo já com
            o código preenchido.
          </>
        ),
        keywords: 'codigo de barras código leitor scanner bipar ean gtin sku laser',
      },
      {
        q: 'Como o sistema me avisa de falta ou vencimento?',
        a: (
          <>
            No topo da aba <strong>Produtos</strong> aparece um quadro amarelo
            de <strong>Alertas</strong> com dois tipos de aviso: produtos cujo
            saldo caiu abaixo do estoque mínimo e lotes que vencem nos próximos
            60 dias. Na tabela, o saldo em vermelho indica abaixo do mínimo e a
            etiqueta <strong>repor</strong> indica que o ponto de reposição foi
            atingido.
          </>
        ),
        keywords: 'alerta estoque baixo falta acabando vencendo validade 60 dias repor aviso',
      },
      {
        q: 'Como faço a contagem/balanço de inventário?',
        a: (
          <>
            Abra o produto e clique em <strong>Balanço</strong>: digite a
            quantidade que você contou de verdade na prateleira e confirme em{' '}
            <strong>Ajustar saldo</strong>. O sistema lança sozinho uma
            movimentação de <strong>ajuste</strong> com a diferença e registra
            no histórico o que foi contado e qual era o saldo anterior. Se já
            estiver batendo, nada é lançado.
          </>
        ),
        keywords: 'balanco balanço inventario inventário contagem fisica física ajustar saldo conferencia conferência',
      },
      {
        q: 'Como faço um pedido de compra e dou baixa no recebimento?',
        a: (
          <>
            Na aba <strong>Compras</strong>, a <strong>Sugestão de
            reposição</strong> já agrupa por fornecedor tudo que está no ponto
            de reposição ou abaixo do mínimo, com a quantidade sugerida —
            clique em <strong>Gerar pedido</strong> e ajuste o que quiser (ou
            monte do zero em <strong>+ Novo pedido</strong>). Quando a
            mercadoria chegar, clique em <strong>Receber</strong>: cada item
            vira uma entrada automática no estoque, com lote, validade e custo
            do pedido. Pedido que não vai mais acontecer pode ser{' '}
            <strong>Cancelado</strong> enquanto estiver aberto.
          </>
        ),
        keywords: 'pedido de compra fornecedor sugestao sugestão reposicao reposição receber recebimento cancelar cotacao',
      },
      {
        q: 'Dá pra lançar a nota do fornecedor sem digitar item por item?',
        a: (
          <>
            Dá: na aba <strong>Compras</strong>, clique em{' '}
            <strong>Importar NF-e</strong>, selecione o arquivo XML da nota (ou
            cole o conteúdo) e clique em <strong>Ler nota</strong>. O sistema
            mostra fornecedor, número da nota e todos os itens com quantidade e
            valor unitário, já tentando casar cada um com um produto seu pelo
            código de barras ou pelo nome. Você confere item a item — pode
            apontar pra outro produto ou mandar criar um novo — e nada é
            gravado até você clicar em <strong>Importar</strong>. Confirmado, a
            entrada cai no estoque como um pedido de compra já recebido.
          </>
        ),
        keywords: 'nfe nf-e nota fiscal xml importar fornecedor entrada automatica automática danfe',
      },
      {
        q: 'Quanto tenho parado em estoque? E quanto gastei no mês?',
        a: (
          <>
            A aba <strong>Relatórios</strong> abre com os últimos 30 dias (você
            muda as datas em <strong>De</strong> e <strong>Até</strong>) e
            mostra quatro visões: <strong>Valor em estoque</strong> (saldo ×
            último custo de cada produto, com o total),{' '}
            <strong>Consumo no período</strong>, <strong>Saídas por
            motivo</strong> e <strong>Entradas por fornecedor</strong>. Produto
            sem custo cadastrado não entra na conta do valor — vale preencher.
          </>
        ),
        keywords: 'relatorio relatórios valorizacao valorização valor em estoque consumo gasto compras fornecedor periodo período',
      },
      {
        q: 'A aplicação de injetáveis dá baixa sozinha no estoque?',
        a: (
          <>
            Sim. Ao salvar uma sessão de injetáveis no prontuário, cada produto
            aplicado gera uma saída automática (motivo <strong>uso</strong>),
            marcada no histórico como <strong>Sessão de injetáveis</strong>. A
            ligação é feita pelo <strong>nome do produto</strong>: se o nome
            digitado na sessão não existir igualzinho no cadastro de estoque, a
            baixa não acontece (e a sessão é salva normalmente). Padronizar os
            nomes resolve.
          </>
        ),
        keywords: 'injetaveis injetáveis toxina botox preenchedor baixa automatica automática prontuario prontuário sessao sessão',
      },
    ],
  },
  {
    id: 'arquivos',
    icon: '🗂️',
    title: 'Arquivos, imagens e análise por IA',
    audience: 'user',
    items: [
      {
        q: 'Pra que serve a página Arquivos do menu?',
        a: (
          <>
            É a central de anexos da clínica: em{' '}
            <Link to="/arquivos">Arquivos</Link> você escolhe o paciente e vê
            tudo o que já foi enviado pra ele — radiografia, foto, scan 3D,
            documento, laudo laboratorial ou outros — com miniatura, tamanho,
            data e descrição. O seletor <strong>Categoria</strong> filtra a
            lista, e cada linha tem os botões de download e excluir.
          </>
        ),
        keywords: 'arquivos anexos documentos central paciente categoria filtro radiografia foto scan laudo',
      },
      {
        q: 'Como envio um arquivo e quais formatos são aceitos?',
        a: (
          <>
            Selecione o paciente, use o bloco <strong>Novo upload</strong>,
            escolha o arquivo, marque a categoria, escreva uma descrição
            (opcional) e clique em <strong>Enviar</strong>. São aceitos JPEG,
            PNG, WEBP, BMP, TIFF, PDF, DICOM e ZIP (pacote de escaneamento 3D),
            até <strong>50 MB</strong> por arquivo. Todo anexo nasce{' '}
            <strong>privado</strong>: o paciente só vê no portal dele o que
            alguém marcar como visível.
          </>
        ),
        keywords: 'upload enviar anexar arquivo formato tamanho 50mb pdf dicom zip privado portal',
      },
      {
        q: 'Como marco pontos numa radiografia ou foto?',
        a: (
          <>
            Clique na miniatura (ou em <strong>🔍 Anotar</strong>) pra abrir o
            visualizador em tela cheia: dá pra dar zoom com o scroll, arrastar
            a imagem e clicar em qualquer ponto pra criar um pin com texto e
            cor (vermelho = problema, amarelo = atenção, verde = OK, azul =
            referência, roxo = medição). As marcações ficam salvas naquele
            arquivo e aparecem pra quem abrir depois.
          </>
        ),
        keywords: 'anotar anotacao anotação pin marcar raio-x radiografia zoom visualizador viewer imagem',
      },
      {
        q: 'O que a análise por IA faz com a imagem?',
        a: (
          <>
            Em qualquer anexo de imagem, o botão <strong>🤖 Analisar IA</strong>{' '}
            manda a foto pro modelo de visão, que devolve um resumo em texto e
            uma lista de achados com grau de confiança — desenhados como
            retângulos sobre a própria imagem. Você pode escrever um{' '}
            <strong>contexto clínico</strong> antes (ex.: “paciente relata dor
            no 26”) pra direcionar o olhar da IA. Analisar de novo a mesma
            imagem em até 24h reaproveita o resultado sem gastar cota; use{' '}
            <strong>↻ Forçar</strong> se quiser uma leitura nova.
          </>
        ),
        keywords: 'ia inteligencia artificial inteligência analisar imagem achados visao visão confianca confiança contexto cache',
      },
      {
        q: 'Existe análise para estética facial, não só odontológica?',
        a: (
          <>
            Sim. No topo do painel de análise você escolhe entre{' '}
            <strong>Odontológica</strong> e{' '}
            <strong>Facial (estética)</strong>. No modo facial os achados falam
            a língua da estética — qualidade da pele, rugas e linhas, perda de
            volume, assimetria, pigmentação, acne, poros, flacidez, vermelhidão
            e cicatrizes — e o contexto ajuda a mirar (ex.: “paciente busca
            melhora de rugas e flacidez no terço médio”).
          </>
        ),
        keywords: 'estetica estética facial pele rugas flacidez harmonizacao harmonização modo analise analise facial',
      },
      {
        q: 'Posso usar a análise da IA como laudo?',
        a: (
          <>
            Não. A IA é apoio de triagem — ela aponta onde olhar, o diagnóstico
            é sempre seu. Depois de ler, clique em{' '}
            <strong>✓ Li e revisei</strong>; só então libera o botão{' '}
            <strong>📋 Anexar ao prontuário</strong>, que cria uma entrada
            permanente no histórico do paciente com resumo, achados e o aviso
            legal. Análise que não te serviu pode ser <strong>Arquivada</strong>{' '}
            (some da lista, mas fica registrada na auditoria).
          </>
        ),
        keywords: 'laudo diagnostico diagnóstico disclaimer revisar li e revisei anexar prontuario prontuário arquivar auditoria',
      },
      {
        q: 'Quem pode analisar, e o que limita o uso?',
        a: (
          <>
            O botão de analisar aparece só pra <strong>dono</strong> e{' '}
            <strong>profissional clínico</strong>, e só em arquivos de imagem —
            PDF, DICOM e ZIP não são analisados. A imagem precisa ter menos de
            20 MB. Cada análise consome a <strong>cota de IA do plano</strong>{' '}
            da clínica (200 usos/mês no Pro, 10.000 no Scale): quando ela
            estoura, o sistema avisa e a análise não roda. Toda análise fica
            registrada em auditoria com quem pediu, quando e sobre qual
            paciente.
          </>
        ),
        keywords: 'quem pode permissao permissão cota limite ia 20mb privacidade lgpd auditoria plano',
      },
    ],
  },
  {
    id: 'ia-atendimento',
    icon: '🤖',
    title: 'IA no atendimento',
    audience: 'user',
    items: [
      {
        q: 'Como analiso uma radiografia com IA?',
        a: (
          <>
            Suba a imagem na aba <strong>Arquivos</strong> do paciente
            (categoria Radiografia) e clique em <strong>Analisar IA</strong>.
            A IA varre quadrante por quadrante e lista achados (cáries,
            restaurações, sisos em desenvolvimento, lesões…) com nível de
            confiança. Você marca <strong>"✓ Li e revisei"</strong> e pode
            anexar a análise ao prontuário oficial. Pode acrescentar
            contexto clínico antes ("paciente com dor no 36") pra guiar a
            análise.
          </>
        ),
        keywords: 'radiografia raio-x análise ia visão achados panorâmica',
      },
      {
        q: 'O que são as sugestões da IA no odontograma?',
        a: (
          <>
            Os achados das análises de imagem aparecem no painel{' '}
            <strong>🤖 Sugestões da IA</strong> do odontograma. Quando a IA
            identifica o dente, o botão <strong>Aplicar</strong> marca o
            estado com 1 clique (sempre decisão sua — nada é aplicado
            automaticamente). Achados sem dente numerado (📷 Imagem) são
            informativos: você localiza no diagrama e marca manualmente. O
            painel mostra só a análise mais recente de cada imagem.
          </>
        ),
        keywords: 'sugestões ia odontograma aplicar achado rx dente',
      },
      {
        q: 'Como marco dentes por voz no Modo Cadeira?',
        a: (
          <>
            No odontograma, abra o <strong>🪑 Modo cadeira</strong> e use o
            botão <strong>🎤 Segure e fale</strong>: dite comandos como
            "dente dezesseis oclusal cárie, vinte e quatro restaurado". A
            IA transcreve, mostra os comandos reconhecidos em etiquetas pra
            você conferir e só aplica o que você confirmar — termos não
            reconhecidos nunca viram marcação. Ideal pra exame inicial sem
            tirar as luvas.
          </>
        ),
        keywords: 'voz voice modo cadeira odontograma ditado dente comando',
      },
      {
        q: 'Posso dar um nome e uma personalidade ao assistente do WhatsApp?',
        a: (
          <>
            Pode. Em{' '}
            <Link to="/configuracoes">Configurações → Comunicação →
            Assistente virtual (IA)</Link>, defina o <strong>nome</strong> com
            que ele se apresenta (ex.: "Sofia"), o <strong>tom</strong> de
            escrita (padrão, formal ou casual) e uma{' '}
            <strong>orientação de estilo</strong> curta. Isso muda só o jeito de
            falar — por segurança, o assistente continua <strong>nunca</strong>{' '}
            falando de preço, tratamento ou diagnóstico, respeitando o opt-in de
            WhatsApp do paciente e passando a conversa para um humano quando o
            assunto foge do que ele pode responder (preço, dúvida clínica,
            cancelamento). O assistente já se apresenta com o{' '}
            <strong>nome da sua clínica</strong> e conhece o{' '}
            <strong>horário de atendimento</strong> (configurado em Agenda →
            Horários): fora do expediente, ele avisa que a equipe retorna no
            próximo horário, mas segue ajudando com o reagendamento.
          </>
        ),
        keywords:
          'assistente virtual agente ia persona nome personalidade tom formal casual estilo whatsapp secretaria configurar sofia horario atendimento expediente saudacao nome da clinica',
      },
      {
        q: 'O assistente do WhatsApp responde endereço e horário da clínica?',
        a: (
          <>
            Sim — além de reagendar, ele responde perguntas{' '}
            <strong>simples e factuais</strong>: horário de atendimento, dias que
            abre, <strong>endereço</strong> e telefone/WhatsApp da clínica. O
            endereço vem da unidade cadastrada em{' '}
            <strong>Configurações → Unidades</strong>; se a unidade não tiver
            endereço preenchido, ele não inventa — passa a conversa para a
            equipe. Preço, dúvida clínica, cancelamento e qualquer coisa que ele
            não saiba <strong>sempre</strong> vão para um humano.
          </>
        ),
        keywords:
          'agente assistente responde endereco endereço horario onde fica como chegar telefone whatsapp unidade factual informacao informação escala humano preco nao sabe',
      },
      {
        q: 'Como assumo uma conversa que a IA está atendendo no WhatsApp?',
        a: (
          <>
            Em <Link to="/atendimentos-ia">Atendimentos IA</Link> (menu
            lateral), abra a conversa e clique em{' '}
            <strong>Assumir conversa</strong>. A partir daí{' '}
            <strong>o agente para de responder aquele paciente</strong> e você
            digita direto pelo painel. Quando terminar, use{' '}
            <strong>Devolver ao agente</strong> pra a IA voltar a atender ou{' '}
            <strong>Encerrar</strong> pra fechar o atendimento. As conversas em
            que a IA pediu ajuda aparecem com o selo <strong>Escalado</strong> e
            também tocam o sino de notificações.
            <br />
            <br />
            <strong>Não achou o botão Assumir conversa?</strong> Ele só aparece
            enquanto o agente está conduzindo. Conversa que chegou como{' '}
            <strong>Aguardando equipe</strong> já veio pra você — pode digitar
            direto, sem assumir nada.
            <br />
            <br />
            A tela tem duas visões (<strong>Lista</strong> e{' '}
            <strong>Quadro</strong>), KPIs no topo (aguardando, espera média,
            resolvidas hoje e % resolvido pela IA), <strong>busca</strong> por
            nome/telefone e o filtro <strong>Minhas</strong> (só as que você
            assumiu). Cada conversa mostra <strong>há quanto tempo espera</strong>{' '}
            (vira âmbar após 10min, vermelho após 30min), quem assumiu e uma{' '}
            <strong>bolinha</strong> quando chega mensagem nova. Dá pra{' '}
            <strong>Assumir/Encerrar</strong> direto do card e usar{' '}
            <strong>respostas rápidas</strong> acima do campo (configuráveis em{' '}
            <strong>Configurações → Assistente virtual</strong>). Quando um paciente
            novo entra na fila, toca um <strong>alerta sonoro</strong> (dá pra
            silenciar no 🔔). Na Lista, <strong>J/K</strong> navegam e{' '}
            <strong>Enter</strong> foca a resposta.
          </>
        ),
        keywords:
          'assumir conversa takeover inbox whatsapp atendimento humano agente ia escalado responder botao nao aparece quadro kanban lista kpi sla busca minhas alerta som mudo respostas rapidas atalho teclado mensagem nova responsavel',
      },
      {
        q: 'Como sei que tem atendimento esperando sem abrir a tela?',
        a: (
          <>
            O item <strong>Atendimentos IA</strong> no menu lateral mostra um{' '}
            <strong>contador</strong> com o número de conversas ativas e um{' '}
            <strong>ponto piscando</strong> quando alguma está{' '}
            <strong>Aguardando equipe</strong>. Cada novo atendimento — ou nova
            mensagem do paciente numa conversa que você assumiu — também{' '}
            <strong>toca o sino</strong> de notificações no topo. Na tela, a aba{' '}
            <strong>Aguardando</strong> filtra só o que precisa de você agora.
            <br />
            <br />
            No modo <strong>copiloto</strong>, quando o paciente pede de fato pra
            remarcar (ou toca num assunto fora do escopo), o agente{' '}
            <strong>não responde sozinho</strong>: ele manda um aviso curto de
            que a equipe vai retornar e deixa a conversa em{' '}
            <strong>Aguardando equipe</strong> pra você confirmar e responder.
            Dúvidas simples e informações ele segue respondendo na hora.
          </>
        ),
        keywords:
          'notificacao sino badge contador menu piscando aguardando equipe aviso novo atendimento whatsapp numero conversas copiloto remarcar nao responde sozinho',
      },
      {
        q: 'O agente pode marcar e remarcar sozinho, sem a equipe confirmar?',
        a: (
          <>
            Pode, se você ligar. Em{' '}
            <strong>Configurações → Assistente virtual</strong>, na seção{' '}
            <strong>Agendamento pelo agente</strong>:
            <br />
            <br />
            <strong>Responder e agir sozinho (autônomo):</strong> no caminho
            feliz (horário livre, paciente cadastrado), o agente marca/remarca e
            confirma com o paciente <strong>sem esperar a equipe</strong>.
            Desligado, ele só prepara o rascunho e a equipe envia (copiloto).
            <br />
            <br />
            <strong>Pode marcar consulta nova:</strong> ligue e escolha quais{' '}
            <strong>serviços</strong> e quais <strong>profissionais</strong> o
            agente pode agendar. Para marcar, a clínica exige um serviço, então
            o agente pergunta <strong>primeiro qual serviço</strong> o paciente
            quer (entre os que você liberou), depois o profissional (se houver
            mais de um) e por fim oferece os horários livres. A duração vem do
            próprio serviço; a <strong>duração padrão</strong> só é usada se o
            serviço não tiver uma.
            <br />
            <br />
            Deixe de fora os serviços que exigem avaliação antes de marcar — para
            esses, o agente encaminha para a equipe.
            <br />
            <br />
            Para o paciente não precisar digitar, o agente manda as opções
            (serviços, profissionais e horários) como uma <strong>lista de
            botões no WhatsApp</strong> — ele toca e pronto. Se o WhatsApp dele
            não mostrar a lista, dá pra responder digitando normalmente. Quando
            há muitos serviços, o agente primeiro pergunta a{' '}
            <strong>área</strong> (ex.: Odontologia, Estética) e depois os
            serviços daquela área.
            <br />
            <br />
            <strong>Antes de marcar ou remarcar de verdade</strong>, o agente
            mostra um resumo (serviço, profissional, dia e hora) e pede a{' '}
            <strong>confirmação do paciente</strong> com os botões Sim/Não —
            nada é agendado sem esse “Sim”. Confirmado, ele avisa que está
            feito e que é só chamar se precisar remarcar.
            <br />
            <br />
            Ele sempre respeita a agenda real — bloqueios, disponibilidade e
            conflitos. E o que fugir do básico (preço, dúvida clínica,
            serviço/profissional fora da lista) continua indo para a equipe.
            <br />
            <br />
            <strong>Paciente sumiu no meio?</strong> Se ele começa a agendar e
            para de responder, o agente manda <strong>um</strong> lembrete
            gentil (“ainda quer agendar?”) alguns minutos depois — respeitando o
            opt-in de WhatsApp. É só um cutucão, não insiste de novo.
            <br />
            <br />
            <strong>Pesquisa de satisfação (NPS do agente):</strong> em{' '}
            <strong>Configurações → Comunicação → Assistente virtual</strong>,
            ligue a pesquisa. Quando o agente conclui um agendamento e a conversa
            encerra, o paciente recebe por WhatsApp uma pergunta de satisfação:
            ele pode <strong>tocar numa nota de 1 a 10</strong> na própria lista
            do WhatsApp, <strong>digitar o número</strong>, ou{' '}
            <strong>abrir o link</strong> e dar a nota de 0 a 10 (o link funciona
            mesmo depois das 24h). A nota aparece como KPI em{' '}
            <strong>Atendimentos IA</strong>. É diferente da pesquisa pós-consulta
            (essa é sobre a experiência com o assistente).
          </>
        ),
        keywords:
          'agente marca remarca sozinho automatico autonomo agendar consulta nova servico profissional disponibilidade bloqueio configurar assistente whatsapp caminho feliz escolher servico primeiro',
      },
      {
        q: 'Como inicio uma conversa nova com um paciente pelo WhatsApp?',
        a: (
          <>
            Em <strong>Atendimentos IA</strong>, clique em{' '}
            <strong>+ Nova conversa</strong>, busque o paciente e confirme. Como o
            WhatsApp só permite que a clínica fale <em>primeiro</em> por meio de um{' '}
            <strong>template aprovado</strong>, o sistema envia a mensagem de
            abertura (template <strong>atendimento_abertura</strong>) pelo número da
            clínica. Quando o paciente responde, abre a janela de 24h e o chat
            livre é liberado no inbox. Exige que o paciente tenha{' '}
            <strong>telefone</strong> e <strong>autorização de WhatsApp</strong>, e
            que o template esteja aprovado na Meta. Disponível para quem tem
            permissão de responder no agente (agent.write).
          </>
        ),
        keywords:
          'iniciar nova conversa paciente whatsapp template abertura atendimento_abertura primeiro contato recepcao recepção janela 24h opt-in',
      },
      {
        q: 'O paciente mandou uma foto ou áudio — consigo ver na inbox?',
        a: (
          <>
            Sim. Quando o paciente envia <strong>imagem, áudio, vídeo ou
            documento</strong> pelo WhatsApp, o anexo aparece direto na conversa em{' '}
            <strong>Atendimentos IA</strong>: a imagem como miniatura (clique para
            ampliar), áudio e vídeo com player, e documento como link para baixar.
            Esse tipo de mensagem <strong>não é respondido pela IA</strong> — vai
            direto para a equipe atender, porque o assistente não "enxerga" o
            arquivo. Os anexos ficam protegidos: só quem tem acesso à inbox
            (agent.read) consegue abrir, e sempre dentro da própria clínica.
          </>
        ),
        keywords:
          'foto imagem audio video documento anexo arquivo paciente whatsapp inbox atendimentos ia ver baixar mídia midia pdf',
      },
      {
        q: 'Dá pra atender pelo celular e receber aviso quando chega mensagem?',
        a: (
          <>
            Dá. O sistema é um app instalável (PWA): no celular, abra o site e
            use <strong>Instalar app</strong> (Android/Chrome) ou, no{' '}
            <strong>iPhone/iPad</strong>, toque em <strong>Compartilhar →
            Adicionar à Tela de Início</strong>. Depois de instalado, o ícone
            mostra um <strong>número</strong> com quantas conversas estão
            aguardando a equipe, e você pode ativar as <strong>notificações</strong>{' '}
            pra ser avisado de novos atendimentos. No iPhone as notificações só
            funcionam com o app <em>instalado na tela inicial</em> (iOS 16.4+) —
            no Safari comum não chega aviso. Útil pra quem faz plantão ou atende
            de casa.
          </>
        ),
        keywords:
          'celular mobile pwa instalar app tela inicial notificacao notificação aviso push badge icone recepcao recepção plantao home office iphone android atender fora',
      },
      {
        q: 'Como ajustar o tempo (SLA) até uma conversa virar "atrasada"?',
        a: (
          <>
            Em <strong>Configurações → Comunicação → Assistente virtual (IA)</strong>,
            no bloco <strong>SLA de atendimento</strong>, você define após quantos
            minutos <em>aguardando a equipe</em> a conversa fica em{' '}
            <strong>amarelo</strong> (atenção) e em <strong>vermelho</strong>{' '}
            (atrasada). Isso colore o cronômetro de cada conversa na inbox e a
            "espera média" no painel. O padrão é <strong>10 min</strong> (amarelo)
            e <strong>30 min</strong> (vermelho); vale para toda a clínica e pode
            ser restaurado ao padrão a qualquer momento. Só dono e gerente editam.
          </>
        ),
        keywords:
          'sla tempo espera atraso amarelo vermelho cronometro cronômetro minutos aguardando equipe configurar inbox atendimentos ia ajustar limite',
      },
      {
        q: 'Como arquivar uma conversa encerrada para limpar a lista?',
        a: (
          <>
            Abra a conversa (ela precisa estar <strong>encerrada</strong>) e clique
            em <strong>🗄 Arquivar conversa</strong>. Ela sai da lista ativa, mas{' '}
            <strong>não é apagada</strong>: o histórico fica guardado. Para ver as
            arquivadas, use o botão <strong>🗄 Arquivadas</strong> no topo da inbox;
            de lá dá pra abrir qualquer uma e <strong>Desarquivar</strong> pra
            trazer de volta. Serve pra manter a lista do dia a dia só com o que
            ainda importa, sem perder registro. Exige permissão de responder no
            agente (agent.write) e fica registrado na auditoria. Dá também pra{' '}
            <strong>arquivar sozinho</strong>: em{' '}
            <strong>Configurações → Comunicação → Assistente virtual (IA)</strong>,
            ligue o <strong>Arquivamento automático</strong> e escolha após
            quantos dias sem atividade as conversas encerradas saem da lista.
            Para arquivar <strong>várias de uma vez</strong>, clique em{' '}
            <strong>☑ Selecionar</strong> no topo, marque as encerradas (ou use
            "Selecionar todas encerradas") e clique em <strong>🗄 Arquivar</strong>.
          </>
        ),
        keywords:
          'arquivar arquivada desarquivar conversa encerrada limpar lista inbox atendimentos ia esconder ocultar historico histórico organizar automático automatico dias',
      },
      {
        q: 'Como vejo o desempenho do assistente (quanto ele resolve sozinho)?',
        a: (
          <>
            Na inbox <strong>Atendimentos IA</strong>, clique em{' '}
            <strong>📊 Métricas</strong> no topo. Abre um resumo dos{' '}
            <strong>últimos 30 dias</strong>: total de conversas, % resolvidas
            pela IA sem precisar da equipe, quantas a equipe assumiu, quantas
            escalaram, agendamentos concluídos e o tempo médio até alguém da
            equipe assumir. Serve pra acompanhar o retorno do assistente e onde
            ele mais precisa de ajuda.
          </>
        ),
        keywords:
          'métricas metricas desempenho resolução resolucao taxa ia agente inbox atendimentos quanto resolve sozinho tempo resposta escalado agendamentos relatório relatorio 30 dias',
      },
      {
        q: 'Preciso entregar o histórico de WhatsApp de um paciente (LGPD) — como?',
        a: (
          <>
            Abra a conversa em <strong>Atendimentos IA</strong> e clique em{' '}
            <strong>⬇ Exportar conversa</strong> (no topo do detalhe). Baixa a
            transcrição completa em arquivo de texto, com paciente, telefone,
            datas e cada mensagem identificada (Paciente, Agente, Equipe). Útil
            para atender o <strong>direito do titular</strong> ou uma auditoria.
            Só quem pode exportar dados de pacientes (permissão{' '}
            <em>patients.export</em>) vê o botão, e a exportação fica registrada
            na auditoria.
          </>
        ),
        keywords:
          'exportar conversa transcrição transcricao lgpd direito titular auditoria baixar txt histórico historico whatsapp paciente atendimentos ia download',
      },
      {
        q: 'A IA pode escrever a resposta pra mim no WhatsApp?',
        a: (
          <>
            Pode sugerir. Com a conversa aberta, clique em{' '}
            <strong>✨ Sugerir resposta</strong>: a IA lê o histórico e{' '}
            <strong>preenche a caixa de texto</strong> com um rascunho.{' '}
            <strong>Nada é enviado</strong> — você lê, corrige o que estiver
            errado e só então clica em <strong>Enviar</strong>. Trate como
            primeira versão, nunca como resposta pronta: o agente não sabe o que
            foi combinado fora do sistema.
            <br />
            <br />
            O rascunho sai no <strong>tom e na identidade da clínica</strong> —
            usa o nome, a persona, o estilo e o horário de atendimento que você
            configurou em{' '}
            <strong>Configurações → Persona do agente</strong> — mas as travas
            de segurança continuam valendo (não inventa preço/horário, não dá
            diagnóstico).
            <br />
            <br />
            Dois cuidados. Se você já tinha digitado alguma coisa,{' '}
            <strong>a sugestão apaga o que estava lá</strong> — clique antes de
            escrever, não depois. E o botão só funciona com a conversa{' '}
            <strong>com a equipe</strong> e dentro da janela de 24h; fora disso
            ele fica apagado.
          </>
        ),
        keywords:
          'sugerir resposta rascunho ia escrever whatsapp copiloto sugestao redigir responder automatico',
      },
      {
        q: 'Paciente mandou WhatsApp e ninguém respondeu — onde isso foi parar?',
        a: (
          <>
            Quando o paciente escreve alguma coisa que não é confirmação nem
            remarcação, a conversa cai em{' '}
            <Link to="/atendimentos-ia">Atendimentos IA</Link> com o selo{' '}
            <strong>Aguardando equipe</strong>, e{' '}
            <strong>o agente para de responder</strong> — a partir dali a
            resposta é humana. Abra e digite direto.
            <br />
            <br />
            O que costuma dar errado:
            <ul>
              <li>
                <strong>Ninguém é avisado.</strong> Esse selo não toca o sino nem
                põe contador no menu — combine na equipe quem abre a tela e de
                quanto em quanto tempo. A lista atualiza sozinha a cada 15
                segundos com a página aberta.
              </li>
              <li>
                <strong>Áudio, foto e documento não aparecem.</strong> Só texto
                entra na caixa. Se o paciente mandou um exame por foto, você não
                vai ver aqui.
              </li>
              <li>
                <strong>Número desconhecido não vira conversa.</strong> O sistema
                precisa casar o telefone com um paciente cadastrado. Número que
                não está em nenhuma ficha — ou que bate com dois pacientes ao
                mesmo tempo — é ignorado em silêncio.
              </li>
            </ul>
            Respondeu e resolveu? Clique em <strong>Encerrar</strong>: é o que
            tira a conversa da fila de pendências.
          </>
        ),
        keywords:
          'mensagem paciente nao respondida aguardando equipe inbox handoff whatsapp perdida sumiu audio foto numero desconhecido',
      },
      {
        q: 'Por que às vezes não consigo responder pelo painel?',
        a: (
          <>
            Duas razões. A conversa precisa estar <strong>com a equipe</strong>{' '}
            — ou porque você clicou em <strong>Assumir conversa</strong>, ou
            porque ela chegou como <strong>Aguardando equipe</strong>. Enquanto
            estiver com o agente, a caixa fica travada. E o WhatsApp só permite
            mensagem livre dentro de{' '}
            <strong>24 horas depois da última mensagem do paciente</strong> — o
            painel mostra o tempo restante e avisa quando a janela fecha; depois
            disso só um modelo aprovado reabre o contato. Acesso ao painel é do
            dono e da recepção; dentista e assistente não entram, porque a caixa
            expõe dados de todos os pacientes.
          </>
        ),
        keywords:
          'nao consigo responder janela 24 horas whatsapp bloqueado template permissao inbox',
      },
      {
        q: 'A IA substitui meu diagnóstico?',
        a: (
          <>
            Não — e o sistema é desenhado pra isso: toda saída de IA é{' '}
            <strong>sugestão com revisão obrigatória</strong> (botões de
            confirmar, disclaimers nos documentos, trilha de auditoria). A
            IA é triagem assistida: aponta onde olhar e poupa digitação. O
            laudo, o diagnóstico e a conduta são sempre do profissional
            responsável. A qualidade também depende do modelo escolhido
            pelo administrador (gpt-4o enxerga mais que o mini) — veja
            "Qual modelo de IA escolher" na seção Integrações.
          </>
        ),
        keywords: 'diagnóstico responsabilidade disclaimer revisão laudo limite ia',
      },
    ],
  },
  {
    id: 'financeiro',
    icon: '💰',
    title: 'Faturas e cobrança',
    audience: 'user',
    items: [
      {
        q: 'Como cobro um paciente?',
        a: (
          <>
            Em <Link to="/faturas">Faturas</Link>, crie a fatura com os
            serviços realizados e emita. Você pode registrar pagamento
            manual (dinheiro/cartão da clínica), parcelar, ou gerar um{' '}
            <strong>link de pagamento online</strong> (PIX, boleto, cartão)
            — o sistema dá baixa sozinho quando o paciente paga.
          </>
        ),
        keywords: 'fatura cobrar pagamento pix link boleto parcelar',
      },
      {
        q: 'Como transformo um plano de tratamento em cobrança?',
        a: (
          <>
            No plano aceito, use <strong>Gerar fatura</strong>: os itens do
            plano viram itens da fatura com os preços acordados (snapshot —
            mudanças futuras de tabela não afetam o combinado).
          </>
        ),
        keywords: 'plano tratamento orçamento fatura gerar cobrança',
      },
      {
        q: 'Paciente atrasou — o sistema cobra sozinho?',
        a: (
          <>
            Sim: a <strong>régua de cobrança</strong> envia lembretes
            automáticos (D-3, +1, +5, +15 — configurável pelo administrador)
            por WhatsApp/e-mail. Você também pode usar{' '}
            <strong>Notificar paciente</strong> manualmente na fatura, com
            anti-spam de 1h.
          </>
        ),
        keywords: 'inadimplência atraso régua lembrete cobrança vencida',
      },
      {
        q: 'Como renegocio uma fatura parcelada?',
        a: (
          <>
            Na fatura, <strong>Renegociar</strong>: informe a nova
            quantidade de parcelas e a primeira data. Parcelas pagas são
            mantidas; o saldo restante é redistribuído.
          </>
        ),
        keywords: 'renegociar parcelas acordo dívida saldo',
      },
      {
        q: 'Como emito nota fiscal (NFS-e)?',
        a: (
          <>
            Com a integração fiscal configurada pelo administrador, a fatura{' '}
            <strong>paga</strong> ganha o botão <strong>Emitir NFS-e</strong>.
            Enquanto a prefeitura processa, a fatura mostra{' '}
            <strong>⏳ Em processamento</strong> (use{' '}
            <strong>Atualizar status</strong>); autorizada, aparece{' '}
            <strong>✓ Autorizada</strong> com o número e o link{' '}
            <strong>Ver nota (PDF)</strong> — o mesmo link fica visível pro
            paciente no portal. Se a prefeitura recusar, o motivo aparece na
            própria fatura.
          </>
        ),
        keywords: 'nota fiscal nfse emitir imposto prefeitura pdf autorizada recusada',
      },
      {
        q: 'Minha NFS-e ficou "Em processamento" e não saiu — o que faço?',
        a: (
          <>
            Abra a fatura e clique em <strong>Atualizar status</strong> no painel
            da NFS-e: o sistema <strong>consulta a prefeitura na hora</strong> e
            resolve a nota — vira <strong>✓ Autorizada</strong> (com número e
            PDF) ou mostra o <strong>motivo da recusa</strong>. Notas antigas às
            vezes travam quando o retorno da prefeitura se perde; esse botão
            força a verificação. Se recusou por causa do{' '}
            <strong>certificado</strong> (ex.: vencido), renove o certificado em
            Integrações → NFS-e e use <strong>Tentar novamente</strong>.
          </>
        ),
        keywords:
          'nfse presa travada em processamento nao saiu nao emitiu atualizar status consultar prefeitura pendente certificado vencido reprocessar',
      },
      {
        q: 'Como funciona o cashback de fidelidade?',
        a: (
          <>
            Em <Link to="/configuracoes">Configurações → Financeiro →
            Fidelidade (cashback)</Link>, o administrador ativa o programa e
            define o <strong>percentual de cashback</strong>. A partir daí,
            toda fatura <strong>paga</strong> credita automaticamente esse
            percentual como <strong>saldo</strong> do paciente — à vista,
            parcial ou no carnê, o crédito entra uma única vez por fatura. O
            saldo aparece no <strong>resumo do paciente</strong>, com o
            extrato de créditos e resgates. O cashback é um incentivo de
            retorno e <strong>não altera os relatórios fiscais</strong>. Se a
            fatura for cancelada ou estornada por completo (nota de crédito
            total), o cashback é <strong>devolvido automaticamente</strong>.
          </>
        ),
        keywords:
          'fidelidade cashback saldo pontos recompensa programa retenção crédito paciente',
      },
      {
        q: 'Como o paciente resgata o saldo (usa o cashback)?',
        a: (
          <>
            Abra a fatura (antes de pagar) e, no bloco{' '}
            <strong>Fidelidade</strong>, informe quanto do saldo usar — ou{' '}
            <strong>Usar máximo</strong>. O valor entra como{' '}
            <strong>desconto</strong> e reduz o total a pagar na hora; o débito
            fica registrado no extrato do paciente. Dá para{' '}
            <strong>remover o resgate</strong> enquanto a fatura não foi paga.
            Cancelar a fatura devolve o saldo automaticamente. Também é possível
            lançar crédito/débito manual pelo <strong>Ajustar saldo</strong> no
            resumo do paciente (cortesia, resgate no balcão).
          </>
        ),
        keywords:
          'resgatar usar saldo cashback desconto fatura checkout ajuste manual crédito débito cortesia remover',
      },
      {
        q: 'O cashback expira?',
        a: (
          <>
            Só se o administrador definir um prazo de validade em{' '}
            <Link to="/configuracoes">Configurações → Financeiro →
            Fidelidade</Link>. Sem prazo, o saldo não expira. Quando há prazo,
            os créditos vencidos saem do saldo automaticamente (lançamento{' '}
            <strong>Expiração</strong> no extrato).
          </>
        ),
        keywords: 'expira validade prazo cashback vencimento expiração saldo',
      },
      {
        q: 'Como lanço uma conta a pagar (despesa da clínica)?',
        a: (
          <>
            Em <Link to="/contas-a-pagar">Contas a pagar</Link>, clique em{' '}
            <strong>Novo lançamento</strong>. Você pode preencher à mão
            (descrição, fornecedor, categoria, valor e vencimento) ou{' '}
            <strong>colar a linha digitável do boleto</strong> e clicar em{' '}
            <strong>Ler</strong> — o sistema preenche o{' '}
            <strong>valor</strong> e o <strong>vencimento</strong> sozinho
            (boletos bancários). Em <strong>guias de arrecadação</strong>{' '}
            (concessionária/convênio/GRU, que começam com 8) o sistema também
            lê o <strong>valor</strong> quando ele vem em reais na linha; como
            essas guias não trazem o <strong>vencimento</strong> no código,
            confirme a data manualmente. Confira sempre antes de salvar.
          </>
        ),
        keywords:
          'conta a pagar despesa fornecedor boleto linha digitável lançar vencimento contas pagar saída',
      },
      {
        q: 'Como registro uma entrada que não é de fatura de paciente?',
        a: (
          <>
            Use <Link to="/outras-receitas">Outras receitas</Link> (menu
            Financeiro). É pra registrar dinheiro que entrou mas{' '}
            <strong>não veio de uma fatura</strong> — aporte do sócio, aluguel
            de sala, reembolso de convênio, venda avulsa de produto. Clique em{' '}
            <strong>Nova receita</strong>, informe descrição, valor e a{' '}
            <strong>data do recebimento</strong> (origem e categoria são
            opcionais). Essas entradas somam no <strong>resultado do mês
            (DRE)</strong> como "Outras receitas", pela data em que o dinheiro
            entrou. As receitas de atendimento continuam vindo das{' '}
            <Link to="/faturas">Faturas</Link>.
          </>
        ),
        keywords:
          'outras receitas entrada avulsa aporte aluguel reembolso convenio venda avulsa receita que nao e fatura dre caixa financeiro',
      },
      {
        q: 'Tem receita fixa todo mês (aluguel, convênio)? Como automatizar?',
        a: (
          <>
            Em <Link to="/outras-receitas">Outras receitas</Link>, aba{' '}
            <strong>Recorrentes</strong>, cadastre a regra (descrição, valor e o
            dia do mês). Todo mês o sistema gera a entrada automaticamente,
            igual às despesas recorrentes. Dá para <strong>Pausar</strong>,{' '}
            <strong>Retomar</strong> ou <strong>Encerrar</strong>, e{' '}
            <strong>Gerar mês atual</strong> na hora. As receitas já geradas são
            mantidas se você remover a regra.
          </>
        ),
        keywords:
          'receita recorrente fixa mensal aluguel convenio mensalidade automatica recorrencia outras receitas',
      },
      {
        q: 'Onde vejo o resumo financeiro do mês (recebido, pago, resultado)?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>, o{' '}
            <strong>Painel financeiro do mês</strong> (no topo) mostra numa visão
            única: <strong>recebido</strong> (faturas pagas + outras receitas),{' '}
            <strong>pago</strong> (despesas), <strong>resultado</strong>, e o que
            está em aberto — <strong>a receber</strong> e <strong>a pagar</strong>.
            Troque o mês no seletor. Dá também pra receber esse resumo por e-mail
            (automático todo dia 1º, em Configurações).
          </>
        ),
        keywords:
          'painel financeiro do mes resumo recebido pago resultado a receber a pagar relatorios dre',
      },
      {
        q: 'Como edito ou corrijo uma despesa já lançada?',
        a: (
          <>
            Na linha da despesa em{' '}
            <Link to="/contas-a-pagar">Contas a pagar</Link>, clique em{' '}
            <strong>Editar</strong>. Dá para corrigir descrição, fornecedor,
            categoria, vencimento e observação a qualquer momento. O{' '}
            <strong>valor</strong> só pode ser alterado enquanto a despesa{' '}
            <strong>não tiver pagamento</strong> — se já houver, desfaça os
            pagamentos primeiro (senão o saldo ficaria inconsistente). Despesa
            cancelada não é editável; reabra antes.
          </>
        ),
        keywords:
          'editar corrigir alterar despesa conta a pagar valor vencimento fornecedor categoria descrição mudar',
      },
      {
        q: 'Posso importar várias contas a pagar de uma planilha?',
        a: (
          <>
            Sim. Em <Link to="/contas-a-pagar">Contas a pagar</Link>, clique em{' '}
            <strong>Modelo</strong> para baixar a planilha-modelo (<strong>.xlsx</strong>,
            já com as colunas certas e uma linha de exemplo), preencha e depois
            clique em <strong>Importar planilha</strong>. Aceita{' '}
            <strong>.xlsx</strong>, <strong>.xls</strong> ou <strong>.csv</strong>.
            As colunas <strong>descrição</strong>, <strong>valor</strong> e{' '}
            <strong>vencimento</strong> são obrigatórias; <strong>fornecedor</strong>{' '}
            e <strong>categoria</strong> são opcionais. Aceita valor no formato
            brasileiro (<em>3.500,00</em>) e data <em>DD/MM/AAAA</em>. Ao final,
            o sistema mostra quantas foram <strong>importadas</strong> e quais
            linhas foram <strong>ignoradas</strong> (com o motivo e o número da
            linha).
          </>
        ),
        keywords:
          'importar planilha csv xlsx excel modelo template contas a pagar despesas em massa lote varias importacao upload baixar modelo',
      },
      {
        q: 'Como pago uma despesa em partes (pagamento parcial)?',
        a: (
          <>
            Em <Link to="/contas-a-pagar">Contas a pagar</Link>, clique em{' '}
            <strong>Registrar pagamento</strong> na despesa e informe o{' '}
            <strong>valor</strong>, a <strong>data</strong> e a forma. Enquanto
            houver saldo, a despesa fica com status <strong>Parcial</strong> e
            mostra quanto já foi pago e quanto falta. Repita a cada parcela
            (ex.: 50% na entrega da reforma, 50% depois) — cada pagamento guarda
            a sua própria data. O botão <strong>Quitar saldo</strong> paga tudo
            o que falta de uma vez. Dá para <strong>remover um pagamento</strong>{' '}
            pelo ✕ na lista, ou <strong>Desfazer pagamentos</strong> para
            estornar tudo e voltar a despesa para "em aberto".
          </>
        ),
        keywords:
          'pagamento parcial parcela parcelar metade 50% reforma pagar em partes saldo restante quitar desfazer estornar despesa conta a pagar',
      },
      {
        q: 'As despesas entram no resultado (DRE) e no caixa?',
        a: (
          <>
            Sim. Cada <strong>pagamento</strong> entra como <strong>saída</strong>{' '}
            no resultado do mês (DRE) pela <strong>data em que foi pago</strong>.
            Em pagamentos parciais, cada parcela cai no mês em que saiu (ex.: 50%
            em um mês, 50% no outro). As despesas <strong>em aberto</strong>,{' '}
            <strong>parciais</strong> e <strong>vencidas</strong> aparecem nos
            totais do topo de <Link to="/contas-a-pagar">Contas a pagar</Link>{' '}
            pelo <strong>saldo que falta</strong>, para você acompanhar o que
            está por vencer — mas só afetam o resultado conforme são pagas.
          </>
        ),
        keywords:
          'despesa dre resultado caixa pagar paga parcial saída fluxo mês relatório contas a pagar saldo',
      },
      {
        q: 'Quem pode excluir uma conta a pagar?',
        a: (
          <>
            Excluir uma despesa é restrito: por padrão <strong>só o dono</strong>{' '}
            (permissão <em>Excluir contas a pagar</em>, que pode ser delegada em{' '}
            <Link to="/configuracoes?section=roles">Papéis e permissões</Link>).
            Além disso, uma despesa que já tem <strong>pagamentos</strong> não
            pode ser apagada — o histórico financeiro é preservado. Para tirá-la
            do caixa, use <strong>Desfazer pagamentos</strong> (estorna) e depois{' '}
            <strong>Cancelar</strong>. Lançar e pagar continuam liberados para o
            gerente; apagar, não.
          </>
        ),
        keywords:
          'excluir apagar despesa conta a pagar permissão dono gerente cancelar rigidez histórico finance.delete quem pode',
      },
      {
        q: 'Posso fotografar/enviar o boleto e deixar a IA preencher?',
        a: (
          <>
            Sim. No <strong>Novo lançamento</strong> de{' '}
            <Link to="/contas-a-pagar">Contas a pagar</Link>, use{' '}
            <strong>Enviar boleto</strong> e escolha um{' '}
            <strong>PDF ou imagem</strong> (foto/print). A IA lê e preenche{' '}
            <strong>valor</strong>, <strong>vencimento</strong> e{' '}
            <strong>fornecedor</strong> — quando a linha digitável bancária é
            legível, o valor e o vencimento vêm dela (cálculo exato), não do
            "chute" da leitura. <strong>Sempre confira</strong> os campos antes
            de salvar: a leitura é um apoio, não substitui a conferência. Essa
            leitura usa IA e conta na sua cota mensal (Configurações → IA).
          </>
        ),
        keywords:
          'boleto ocr foto pdf imagem ia ler automatico preencher despesa conta a pagar digitalizar escanear leitura',
      },
      {
        q: 'Posso guardar o boleto ou a nota junto da despesa?',
        a: (
          <>
            Sim. Cada despesa em{' '}
            <Link to="/contas-a-pagar">Contas a pagar</Link> tem a coluna{' '}
            <strong>Boleto</strong>: clique em <strong>Anexar</strong> e escolha
            um <strong>PDF ou imagem</strong> (até 10 MB) do boleto ou nota.
            Depois o botão vira <strong>Ver</strong>, que abre o documento numa
            aba nova, e o <strong>✕</strong> remove. Quando você envia um boleto
            para a leitura automática no <strong>Novo lançamento</strong>, esse
            mesmo arquivo já fica anexado à despesa ao salvar — não precisa subir
            de novo. É o comprovante no mesmo lugar da conta, pronto para a
            conciliação e para o contador.
          </>
        ),
        keywords:
          'anexar boleto nota documento comprovante despesa conta a pagar arquivo pdf imagem guardar ver baixar anexo',
      },
      {
        q: 'Como cadastro contas fixas que se repetem todo mês?',
        a: (
          <>
            Em <Link to="/contas-a-pagar">Contas a pagar</Link>, aba{' '}
            <strong>Recorrentes</strong>: cadastre a conta fixa (aluguel,
            salário, assinatura) com <strong>valor</strong> e{' '}
            <strong>dia do vencimento</strong> (1 a 28). Todo mês o sistema{' '}
            <strong>gera o lançamento automaticamente</strong> na lista de
            Contas a pagar, no dia escolhido — alimentando também o{' '}
            <strong>fluxo de caixa</strong>. Dá pra <strong>pausar</strong>{' '}
            (para temporariamente), <strong>retomar</strong>,{' '}
            <strong>encerrar</strong> (não gera mais) e usar{' '}
            <strong>Gerar mês atual</strong> para criar o do mês corrente na
            hora. Remover a regra <strong>mantém</strong> os lançamentos já
            gerados. A geração automática é por conta do administrador habilitar
            no servidor; enquanto isso, o botão <strong>Gerar mês atual</strong>{' '}
            resolve manualmente.
          </>
        ),
        keywords:
          'despesa recorrente conta fixa aluguel salario assinatura mensal automatico gerar todo mes recorrencia pausar encerrar dia vencimento contas a pagar',
      },
    ],
  },
  {
    id: 'comunicacao',
    icon: '💬',
    title: 'WhatsApp e campanhas',
    audience: 'user',
    items: [
      {
        q: 'Como conecto o número de WhatsApp da própria clínica?',
        a: (
          <>
            Em <strong>Configurações → Comunicação → WhatsApp Business</strong>,
            abra <strong>“Como conectar o WhatsApp da clínica”</strong> e siga o
            passo a passo: ter uma conta WhatsApp Business (WABA) na Meta com o
            número verificado, copiar o <strong>Phone Number ID</strong> e um{' '}
            <strong>Access Token</strong> no painel da Meta (WhatsApp → API
            Setup), colar nos campos, ativar e usar{' '}
            <strong>Testar conexão</strong>. Importante: ao migrar o número para
            a API ele <strong>sai do app</strong> do celular (a equipe passa a
            atender pela tela Atendimentos IA), e a API <strong>não faz
            ligação</strong> de voz/vídeo. Sem conectar um número próprio, os
            envios saem pelo número da plataforma.
          </>
        ),
        keywords:
          'conectar whatsapp proprio numero clinica waba meta phone number id access token api setup migrar integracao integração byo',
      },
      {
        q: 'Quais mensagens o sistema envia pelo WhatsApp?',
        a: (
          <>
            Lembrete/confirmação de consulta, aviso de fatura, anexo
            compartilhado e campanhas que você criar. As campanhas usam
            modelos aprovados no WhatsApp (Meta), com variáveis como o nome
            do paciente e o nome da clínica.
          </>
        ),
        keywords: 'whatsapp mensagem automática template texto modelo',
      },
      {
        q: 'Como faço uma campanha (ex.: retorno de avaliação)?',
        a: (
          <>
            Em <Link to="/campanhas">Campanhas</Link>, escolha um dos públicos
            prontos — <strong>Consultas amanhã</strong>,{' '}
            <strong>Aniversariantes do mês</strong>,{' '}
            <strong>Aniversariantes da equipe</strong> ou{' '}
            <strong>Reativação (180+ dias sem vir)</strong> —, confira a prévia
            de quem vai receber, escreva a mensagem (ou carregue um template
            salvo) e dispare. Há limite de envios por hora pra proteger seu
            número. A prévia mostra <strong>quantos serão realmente
            contatados</strong>: campanhas a pacientes só vão pra quem tem
            telefone <em>e</em> opt-in de WhatsApp — quem não autorizou é
            ignorado (LGPD). Para aumentar o alcance, acompanhe a{' '}
            <Link to="/consentimento">cobertura de opt-in</Link>.
          </>
        ),
        keywords: 'campanha disparo marketing aniversário reativação inativos retorno opt-in consentimento lgpd alcance contatados ignorado',
      },
    ],
  },
  {
    id: 'sala-espera',
    icon: '🖥️',
    title: 'Sala de espera e check-in',
    audience: 'user',
    items: [
      {
        q: 'Como funciona o check-in por QR code?',
        a: (
          <>
            Imprima o QR de <strong>Configurações → Sala de espera</strong>.
            O paciente escaneia ao chegar e entra na fila — a recepção vê o
            status “chegou” na agenda em tempo real.
          </>
        ),
        keywords: 'check-in qr code chegada fila recepção',
      },
      {
        q: 'O que aparece na TV da sala de espera?',
        a: (
          <>
            A fila de atendimento em tempo real + vídeos/imagens
            institucionais que o administrador configurar em{' '}
            <strong>Configurações → Sala de espera — Mídia</strong>.
          </>
        ),
        keywords: 'tv painel sala espera vídeo fila chamada',
      },
    ],
  },

  // ───────────────────── O PACIENTE FORA DA CLÍNICA ─────────────────────
  // Canais em que o paciente age sozinho: marca pela internet e responde
  // a pesquisa depois do atendimento.
  {
    id: 'agendamento-online',
    icon: '🌐',
    title: 'Perfil público e agendamento online',
    audience: 'both',
    items: [
      {
        q: 'O que é a página pública do profissional?',
        a: (
          <>
            É uma página aberta, sem login, no endereço{' '}
            <strong>/p/&lt;id do profissional&gt;</strong>, que mostra a foto,
            o nome, a função (“Cirurgião-dentista”), a bio, a especialidade, o
            registro profissional, o nome da clínica, o link do LinkedIn e um
            botão <strong>Agendar consulta</strong>. Só profissionais ativos
            com papel Dono ou Dentista têm página — os demais dão “não
            encontrado”. Ela existe pra ser colada onde o paciente já está: QR
            code no balcão, cartão de visita, bio do Instagram e assinatura de
            e-mail.
          </>
        ),
        keywords:
          'perfil publico pagina profissional link qr code cartao bio instagram assinatura /p',
      },
      {
        q: 'Onde eu edito o que aparece no meu perfil público?',
        a: (
          <>
            Em <Link to="/perfil">Meu perfil → Dados pessoais</Link>: a{' '}
            <strong>foto</strong> (enviar arquivo ou “Importar do LinkedIn”), a{' '}
            <strong>Bio (perfil público)</strong> de até 500 caracteres, a{' '}
            <strong>Especialidade</strong>, o <strong>LinkedIn (URL)</strong> e,
            no bloco “Dados profissionais”, o conselho, o número e a UF.
            Atenção: na página pública sai o número do <strong>CRO</strong> —
            outros conselhos (CREFITO, CRP, CRM…) ainda não são exibidos ali,
            embora saiam normalmente em receitas e atestados.
          </>
        ),
        keywords:
          'editar perfil bio linkedin foto avatar cro conselho especialidade meu perfil',
      },
      {
        q: 'Como o paciente marca sozinho pela internet?',
        a: (
          <>
            Ele clica em <strong>Agendar consulta</strong> na sua página
            pública (ou vai direto em{' '}
            <strong>/p/&lt;id do profissional&gt;/agendar</strong>) e preenche
            quatro campos: nome completo, telefone com WhatsApp, dia e horário.
            O campo <strong>Motivo</strong> é opcional. Os horários oferecidos
            são blocos de 1 hora, calculados a partir do expediente da clínica e
            do seu expediente próprio, já descontando consultas e bloqueios — e
            só a partir do dia seguinte.
          </>
        ),
        keywords:
          'agendamento online paciente marca sozinho self service agendar internet horario livre',
      },
      {
        q: 'O que acontece no sistema quando alguém agenda pela internet?',
        a: (
          <>
            O sistema procura um paciente da clínica com aquele{' '}
            <strong>mesmo telefone</strong> (comparando só os números, sem
            máscara): se achar, reaproveita o cadastro; se não achar, cria um
            paciente novo com nome e telefone. A consulta entra na{' '}
            <Link to="/agenda">Agenda</Link> com status{' '}
            <strong>Agendada</strong> (não “confirmada”), duração de 1 hora e o
            motivo <strong>“Agendamento online”</strong> quando o paciente não
            escreve nada. Dono, gerente e recepção recebem a notificação{' '}
            <strong>“Novo agendamento”</strong> no sino.
          </>
        ),
        keywords:
          'agendamento online duplicado telefone status agendada notificacao novo agendamento origem',
      },
      {
        q: 'O que a recepção precisa conferir depois de um agendamento online?',
        a: (
          <>
            Quatro coisas: <strong>ligar/mandar WhatsApp confirmando</strong>{' '}
            (a tela avisa o paciente que a clínica confirma em até 24h úteis, e
            esse contato é manual); <strong>ajustar serviço e duração</strong>,
            porque o paciente não escolhe procedimento e o horário entra sempre
            com 1 hora; <strong>completar o cadastro</strong> em{' '}
            <Link to="/pacientes">Pacientes</Link> (CPF, e-mail, nascimento,
            endereço); e <strong>registrar o consentimento de WhatsApp</strong>{' '}
            na aba Resumo do paciente — sem o opt-in os lembretes automáticos
            não saem justamente pra quem veio pela internet.
          </>
        ),
        keywords:
          'recepcao conferir agendamento online confirmar servico duracao cadastro incompleto opt-in',
      },
      {
        q: 'O que é a pré-anamnese que o paciente preenche antes?',
        a: (
          <>
            É um questionário de 6 passos (queixa principal, alergias,
            medicações, condições de saúde, hábitos e revisão) que o paciente
            responde <strong>pelo portal do paciente</strong>, não pela página
            de agendamento. O card <strong>“Pré-anamnese disponível”</strong> só
            aparece pra quem tem consulta futura marcada — por isso mande o link
            do portal depois de agendar.
          </>
        ),
        keywords:
          'pre anamnese pre-anamnese questionario antes da consulta portal alergias medicacoes habitos',
      },
      {
        q: 'Onde vejo o que o paciente respondeu na pré-anamnese?',
        a: (
          <>
            A resposta cai em dois lugares: alimenta o{' '}
            <strong>Histórico médico</strong> do paciente (alergias, medicações,
            condições e hábitos passam a gerar os alertas coloridos do topo do
            prontuário) e cria um registro do tipo <strong>Anamnese</strong> na
            aba <strong>Prontuário</strong>, com a marcação de que veio do
            portal. Revise antes de usar clinicamente — foi o paciente que
            digitou.
          </>
        ),
        keywords:
          'pre anamnese resposta onde ver prontuario historico medico alertas alergia anamnese portal',
      },
    ],
  },
  {
    id: 'satisfacao',
    icon: '⭐',
    title: 'Pesquisa de satisfação (NPS)',
    audience: 'both',
    items: [
      {
        q: 'Quando a pesquisa é enviada para o paciente?',
        a: (
          <>
            Assim que você <strong>conclui o atendimento</strong> na agenda, o
            sistema agenda o envio por WhatsApp com o atraso que a clínica
            definiu. É uma pesquisa por consulta concluída — o mesmo paciente
            recebe de novo a cada novo atendimento. O envio só acontece se ele
            tiver telefone cadastrado e o consentimento de WhatsApp autorizado
            na ficha.
          </>
        ),
        keywords:
          'pesquisa de satisfação satisfacao nps quando envia gatilho concluir atendimento whatsapp automático automatico',
      },
      {
        q: 'O que o paciente recebe e o que ele vê?',
        a: (
          <>
            Ele recebe uma mensagem no WhatsApp com um link pessoal (o endereço{' '}
            <strong>/pesquisa/</strong> seguido de um código único). Ao abrir,
            vê a pergunta “Como foi seu atendimento?”, dá a nota de{' '}
            <strong>1 a 5 estrelas</strong> e pode escrever um comentário — a
            nota é obrigatória, o comentário não. Ele responde uma única vez: se
            abrir o link de novo, aparece só a tela de agradecimento.
          </>
        ),
        keywords:
          'link da pesquisa token estrelas nota comentário comentario paciente responder agradecimento',
      },
      {
        q: 'Onde eu ativo e configuro a pesquisa?',
        a: (
          <>
            Em <strong>Configurações → Pesquisa de satisfação</strong> (dono ou
            gerente). Marque <strong>Ativar pesquisa de satisfação</strong>,
            escolha o <strong>Enviar depois de</strong> (1, 2 ou 4 horas, 1 dia
            ou 2 dias após o atendimento) e ajuste a{' '}
            <strong>Mensagem do WhatsApp</strong>, onde valem as variáveis{' '}
            {'{nome}'}, {'{clinica}'} e {'{link}'}. Se apagar a mensagem, volta
            o texto padrão.
          </>
        ),
        keywords:
          'configurar ativar pesquisa satisfação satisfacao atraso delay mensagem whatsapp variáveis variaveis',
      },
      {
        q: 'Onde vejo o resultado?',
        a: (
          <>
            No bloco <strong>Resultados</strong>, na mesma tela de{' '}
            <strong>Configurações → Pesquisa de satisfação</strong>: ele mostra
            a <strong>média de estrelas</strong> e o total de respostas e, logo
            abaixo, as respostas que vieram com comentário, da mais recente para
            a mais antiga. É uma média de 1 a 5 — não há painel separado nem
            gráfico de evolução, e as notas sem comentário entram na média mas
            não aparecem na lista.
          </>
        ),
        keywords:
          'resultado nps média media nota estrelas respostas comentários comentarios relatório relatorio',
      },
      {
        q: 'Recebi uma nota baixa — o que faço?',
        a: (
          <>
            O sistema não dispara alerta nem cria tarefa automática nesse caso,
            então vale abrir o bloco <strong>Resultados</strong> uma vez por
            semana. A lista mostra as estrelas e o comentário, mas não
            identifica quem respondeu: use o comentário como pista e cruze com
            os atendimentos daqueles dias na <Link to="/agenda">Agenda</Link>{' '}
            para fazer o contato. Não é possível responder pela própria pesquisa
            nem marcar o caso como resolvido.
          </>
        ),
        keywords:
          'nota baixa detrator reclamação reclamacao paciente insatisfeito alerta retorno follow-up',
      },
    ],
  },

  // ───────────── ACOMPANHAMENTO, MODELOS E PREFERÊNCIAS ─────────────
  // Depois que a operação roda: medir, buscar modelo pronto e ajustar o
  // que chega até você.
  {
    id: 'relatorios',
    icon: '📊',
    title: 'Relatórios e exportações',
    audience: 'user',
    items: [
      {
        q: 'Quais relatórios existem?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>: receita (faturado,
            recebido e pendente), receita por forma de pagamento, top serviços,
            novos pacientes, <strong>carteira em aberto</strong> por faixa de
            atraso, <strong>fluxo de caixa projetado</strong>,{' '}
            <strong>tempo de espera</strong>, o painel de{' '}
            <strong>Metas do mês</strong> (progresso da clínica + ranking por
            profissional), o <strong>Funil comercial</strong>, os{' '}
            <strong>alertas de negócio</strong>, a{' '}
            <strong>retenção por coorte + LTV</strong>, e o painel fiscal (DRE
            mensal, recibos por profissional e resumo de NFS-e). A{' '}
            <strong>produtividade por profissional</strong> fica em{' '}
            <Link to="/equipe">Equipe</Link>.
          </>
        ),
        keywords:
          'relatório faturamento produtividade dre fluxo de caixa aging inadimplencia tempo de espera metas progresso ranking',
      },
      {
        q: 'O que é o Fluxo de caixa consolidado?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>, o{' '}
            <strong>Fluxo de caixa consolidado</strong> junta, mês a mês, o que
            a clínica tem <strong>a receber</strong> (faturas em aberto, pelo
            vencimento) menos o que tem <strong>a pagar</strong> (despesas de{' '}
            <Link to="/contas-a-pagar">Contas a pagar</Link>, pelo vencimento),
            mostrando o <strong>saldo do mês</strong> e o{' '}
            <strong>saldo acumulado</strong> projetado. A linha{' '}
            <strong>Vencido</strong> junta o que já passou do vencimento dos
            dois lados. Dá pra escolher a janela (3, 6 ou 12 meses). Serve pra
            enxergar com antecedência os meses em que o acumulado fica negativo
            (aperto de caixa). É uma <strong>projeção otimista</strong>: assume
            que tudo é pago e recebido em dia e não desconta inadimplência.
          </>
        ),
        keywords:
          'fluxo de caixa consolidado a receber a pagar saldo acumulado projeção capital de giro aperto caixa despesas faturas vencimento mês',
      },
      {
        q: 'Como funciona o painel de Metas do mês?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>, o painel{' '}
            <strong>Metas do mês</strong> mostra o faturamento{' '}
            <strong>realizado × a meta</strong> da clínica, com barra de
            progresso e uma <strong>projeção de fechamento</strong>: pelo ritmo
            dos dias já decorridos, quanto o mês deve fechar e quantos % da meta
            isso representa. Abaixo vem o <strong>ranking por profissional</strong>,
            cada um com realizado, meta individual, progresso e projeção.
            A meta da clínica é definida em{' '}
            <strong>Configurações → Meta mensal</strong> (só o dono); sem meta
            da clínica, o painel soma as metas individuais da equipe. As metas
            por profissional ficam em <Link to="/equipe">Equipe</Link>. Escolha
            o mês/ano no filtro do painel.
          </>
        ),
        keywords:
          'meta metas mês clínica progresso projeção fechamento ritmo ranking profissional faturamento objetivo bater meta quanto falta ritmo acompanhamento tempo real',
      },
      {
        q: 'O que mostram o Funil comercial e os alertas de negócio?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>, o{' '}
            <strong>Funil comercial</strong> acompanha os planos de tratamento
            no período em três estágios — <strong>propostos → aceitos →
            concluídos</strong> — com a contagem, o valor em R$ de cada estágio
            e as <strong>taxas de conversão</strong> (quantos % dos propostos
            foram aceitos, e quantos % dos aceitos foram concluídos). No topo,
            os <strong>alertas</strong>: <strong>Receita do mês</strong>{' '}
            comparada com o mês anterior (fica vermelho se cair muito) e{' '}
            <strong>Ocupação da agenda</strong> da semana (horas agendadas vs
            capacidade; fica amarelo se tiver muita agenda livre). São sinais
            pra agir — não relatório fiscal.
          </>
        ),
        keywords:
          'funil comercial conversão proposto aceito concluído plano tratamento taxa alerta queda receita mês anterior agenda ociosa ocupação capacidade livre vendas',
      },
      {
        q: 'Como leio a Retenção por coorte e o LTV?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>, o painel{' '}
            <strong>Retenção por coorte &amp; LTV</strong> agrupa os pacientes
            pelo <strong>mês da 1ª consulta</strong> (cada linha é uma coorte).
            As colunas <strong>M0, M1, M2…</strong> mostram o{' '}
            <strong>percentual da coorte que voltou</strong> naquele mês depois
            da entrada — M0 é sempre 100% (o mês em que entraram). Quanto mais
            verde, melhor a retenção; células vazias (·) são meses que ainda não
            aconteceram. O <strong>LTV médio</strong> é o valor gerado por
            paciente (soma dos atendimentos concluídos, na janela de 12 meses).
            Serve pra ver se a clínica <strong>segura</strong> o paciente ao
            longo do tempo e quanto cada um vale — é analytics, não relatório
            fiscal.
          </>
        ),
        keywords:
          'retenção coorte cohort ltv valor vitalício paciente volta voltou retido retenido primeira consulta heatmap mapa calor fidelização analytics lifetime value',
      },
      {
        q: 'Como exporto meus dados?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>, o bloco de receita tem
            botões <strong>Excel</strong> e <strong>CSV</strong>, e os
            relatórios fiscais exportam <strong>PDF</strong> e{' '}
            <strong>Excel</strong>. Nas listagens de{' '}
            <Link to="/pacientes">Pacientes</Link> e{' '}
            <Link to="/faturas">Faturas</Link> a exportação fica na própria
            tela. A exportação é sua, sem pedir ao suporte — vale também se um
            dia decidir sair. <strong>Exportar a base de pacientes</strong>{' '}
            (Excel/CSV) é restrito ao <strong>dono</strong> por padrão (protege
            sua carteira de clientes) — o dono pode liberar pra outro papel em{' '}
            <strong>Configurações → Papéis e permissões</strong>. Toda
            exportação da base fica registrada na{' '}
            <strong>Trilha de auditoria</strong>.
          </>
        ),
        keywords: 'exportar excel csv pdf backup baixar dados dono base pacientes carteira clientes leads permissão auditoria restrito',
      },
      {
        q: 'Como recebo o resumo financeiro do mês por e-mail?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>, ao lado dos botões de
            exportação, o botão <strong>E-mail</strong> envia o{' '}
            <strong>resumo financeiro do mês atual</strong>: receita realizada,
            despesas pagas, resultado, meta, e o que está{' '}
            <strong>a receber</strong> e <strong>a pagar</strong> em aberto. Ao
            clicar, abre uma janela onde você escolhe o destino — em branco vai
            pro <strong>seu</strong> e-mail, ou pode mandar direto pro{' '}
            <strong>contador</strong>. Dá também pra deixar{' '}
            <strong>automático</strong>: em{' '}
            <strong>Configurações → Resumo financeiro por e-mail</strong>, ligue
            o envio e todo <strong>dia 1º</strong> a clínica recebe o fechamento
            do mês anterior sozinho (pro dono ou pro contador). Como
            são dados financeiros, cada envio fica registrado na{' '}
            <strong>Trilha de auditoria</strong>. Disponível para dono e gerente.
          </>
        ),
        keywords:
          'relatório financeiro email enviar resumo mês receita despesa resultado contador dono gerente fechamento mensal mandar por e-mail',
      },
      {
        q: 'Onde vejo a comissão e o progresso de meta de cada profissional?',
        a: (
          <>
            Em <Link to="/relatorios">Relatórios</Link>, no painel fiscal, abra{' '}
            <strong>Recibos por profissional</strong> e escolha o mês. A tabela
            traz a coluna <strong>Comissão</strong> (calculada sobre a receita{' '}
            <strong>paga</strong> atribuída ao profissional, pelo % cadastrado) e{' '}
            <strong>Meta / progresso</strong> (realizado no mês × meta
            mensal). Dá pra exportar em <strong>PDF</strong> e{' '}
            <strong>Excel</strong>. Quem não tem % ou meta cadastrados aparece
            sem valor nessas colunas.
          </>
        ),
        keywords:
          'comissão comissao meta progresso recibos profissional realizado atingiu bateu meta percentual relatorio fiscal pagar comissao quanto ganhou',
      },
    ],
  },
  {
    id: 'conciliacao',
    icon: '⚖️',
    title: 'Conciliação bancária',
    audience: 'user',
    items: [
      {
        q: 'Para que serve a Conciliação bancária?',
        a: (
          <>
            Em <Link to="/conciliacao">Conciliação</Link> você importa o
            extrato do banco e confere, lançamento por lançamento, se cada{' '}
            <strong>entrada</strong> corresponde a uma fatura paga e cada{' '}
            <strong>saída</strong> a uma conta a pagar quitada. Fecha o ciclo do
            financeiro dos dois lados — o que entrou e o que saiu do banco bate
            com o que foi registrado — sem planilha paralela.
          </>
        ),
        keywords: 'conciliação bancária extrato banco financeiro bater recebimento pagamento saída despesa ofx csv',
      },
      {
        q: 'Como importo o extrato?',
        a: (
          <>
            Clique em <strong>Importar extrato</strong> e selecione o arquivo{' '}
            <strong>OFX</strong> (padrão dos bancos) ou <strong>CSV</strong>.
            O sistema lê data, valor e descrição de cada lançamento e ignora
            linhas repetidas — pode reimportar o mesmo arquivo sem duplicar. No
            CSV ele reconhece as colunas pelo cabeçalho (data, valor,
            descrição); quando a coluna de descrição vem <strong>vazia</strong>{' '}
            (comum em bancos digitais como Stone), ele monta o rótulo a partir
            do <strong>tipo</strong> e de <strong>quem recebeu/pagou</strong>{' '}
            (ex.: <em>Pix · Fulano de Tal</em>). Aceita o formato brasileiro
            (ex.: <em>15/10/2026; 100,00; PIX</em>). Um lançamento ainda não
            conciliado pode ser removido no <strong>Excluir</strong> — útil pra
            limpar uma importação antiga e reimportar.
          </>
        ),
        keywords: 'importar extrato ofx csv arquivo banco upload duplicado stone descrição vazia excluir tipo pix',
      },
      {
        q: 'Como concilio uma entrada com a fatura?',
        a: (
          <>
            Cada entrada pendente mostra <strong>Conciliar</strong>: o sistema
            sugere, com o mesmo valor e datas próximas, faturas{' '}
            <strong>pagas à vista</strong>, <strong>pagamentos de parcela</strong>{' '}
            (carnê) e também <strong>faturas em aberto</strong> (etiqueta{' '}
            <em>em aberto</em>). Se você escolher uma fatura em aberto, o sistema{' '}
            <strong>registra o recebimento sozinho</strong> (dá baixa na fatura
            na data do crédito) — tudo num passo. Confirme a correta e o
            lançamento vira <strong>Conciliada</strong>. Entradas que não são da
            clínica (ex.: transferência própria) podem ser marcadas como{' '}
            <strong>Ignorar</strong>. Dá para <strong>Desfazer</strong> a
            conciliação a qualquer momento.
          </>
        ),
        keywords: 'conciliar casar fatura paga sugestão ignorar desfazer pendente',
      },
      {
        q: 'Como concilio uma saída (débito) com uma conta a pagar?',
        a: (
          <>
            Débitos do extrato (saídas) agora também conciliam. Clique em{' '}
            <strong>Conciliar</strong> na saída: o sistema sugere, com o mesmo
            valor, tanto <strong>pagamentos já lançados</strong> (etiqueta{' '}
            <em>despesa</em>) quanto <strong>contas a pagar em aberto</strong>{' '}
            (etiqueta <em>conta a pagar</em>). Se escolher uma conta em aberto,
            o sistema <strong>registra o pagamento sozinho</strong> (na data do
            débito) e já dá a baixa — num passo só. Confirme e o débito vira{' '}
            <strong>Conciliada</strong>. Ao <strong>Desfazer</strong>, se a baixa
            foi feita pela conciliação, o pagamento é estornado junto. Vale o
            mesmo <strong>Ignorar</strong> (ex.: tarifa, transferência própria).
          </>
        ),
        keywords: 'conciliar saída débito despesa conta a pagar pagamento fornecedor casar banco extrato',
      },
    ],
  },
  {
    id: 'marketplace',
    icon: '🛍️',
    title: 'Marketplace de modelos',
    audience: 'user',
    items: [
      {
        q: 'O que é o Marketplace?',
        a: (
          <>
            É a vitrine onde clínicas Kevins Clínicas compartilham modelos
            prontos de pacote de tratamento. Nela você vê o que outras
            clínicas publicaram — com autor, nota média e número de instalações
            — e traz pra sua clínica em um clique, sem montar o pacote do zero.
            {' '}<em>(Recurso em preparação — ainda não disponível no menu.)</em>
          </>
        ),
        keywords: 'marketplace loja vitrine modelos templates pacotes compartilhar comunidade',
      },
      {
        q: 'Como encontro um modelo que sirva pra mim?',
        a: (
          <>
            Use a busca por nome, descrição ou categoria, o filtro por tipo e a
            ordenação: <strong>Mais instalados</strong>,{' '}
            <strong>Mais recentes</strong> ou{' '}
            <strong>Melhor avaliados</strong>. Clique no card pra abrir os
            detalhes e conferir a chamada, a descrição, os procedimentos
            incluídos com quantidade e o preço sugerido antes de decidir.
          </>
        ),
        keywords: 'buscar filtrar ordenar procurar pesquisa mais instalados recentes melhor avaliados detalhes',
      },
      {
        q: 'Como instalo um modelo na minha clínica?',
        a: (
          <>
            Abra o modelo e clique em <strong>Instalar nesta clínica</strong>.
            Ele é copiado pra você em{' '}
            <Link to="/planos/pacotes">Planos → Pacotes</Link>, onde pode ser editado à
            vontade — preço, itens, textos — sem afetar o original nem a
            clínica que publicou. Modelos publicados pela sua própria clínica
            não mostram o botão (já são seus).
          </>
        ),
        keywords: 'instalar baixar copiar usar modelo pacote planos template importar',
      },
      {
        q: 'Como avalio ou denuncio um modelo?',
        a: (
          <>
            Dentro do modelo, clique nas estrelas em{' '}
            <strong>Avalie este template</strong> pra dar de 1 a 5 — sua nota
            entra na média que os outros veem. Se o conteúdo for impróprio,
            use <strong>Reportar conteúdo</strong>, descreva o motivo
            (opcional) e confirme: a equipe Kevins Clínicas revisa e pode
            remover o material. Você não avalia nem reporta o que a sua própria
            clínica publicou.
          </>
        ),
        keywords: 'avaliar nota estrelas rating denunciar reportar flag conteudo conteúdo improprio impróprio',
      },
      {
        q: 'Como publico um modelo meu — e o que vai junto?',
        a: (
          <>
            Clique em <strong>+ Publicar meu template</strong> (disponível pra
            dono e profissional clínico), escolha um dos seus pacotes ativos e
            confirme. É publicada uma <strong>cópia congelada</strong> do
            pacote: nome, descrição, categoria, duração, chamada, benefícios,
            preços sugeridos e a lista de procedimentos, com o nome da sua
            clínica como crédito. <strong>Nenhum dado de paciente é
            compartilhado</strong> — o marketplace só enxerga o modelo, nunca
            prontuário, agenda ou cadastro. Editar o pacote depois não altera o
            que já está publicado.
          </>
        ),
        keywords: 'publicar compartilhar meu template pacote autoria credito crédito privacidade dados do paciente lgpd',
      },
    ],
  },
  {
    id: 'notificacoes-perfil',
    icon: '🔔',
    title: 'Notificações e seu perfil',
    audience: 'both',
    items: [
      {
        q: 'Para que serve o sininho no topo da tela?',
        a: (
          <>
            Ele avisa o que aconteceu na clínica enquanto você estava em outra
            tela. A bolinha vermelha mostra quantas notificações você ainda não
            leu (acima de nove aparece <strong>9+</strong>) e o número se
            atualiza sozinho a cada 30 segundos — não precisa recarregar a
            página. Clique no sino pra abrir a lista das 20 mais recentes.
          </>
        ),
        keywords: 'sino sininho notificacao badge nao lidas alerta aviso topo',
      },
      {
        q: 'Quais avisos aparecem no sino?',
        a: (
          <>
            <strong>Novo agendamento</strong>, <strong>Agendamento
            remarcado</strong> e <strong>Consulta confirmada</strong> (esse
            último também vai pro profissional da consulta) — todos pra dono,
            gerente e recepção; <strong>“Atendimento no WhatsApp precisa de
            você”</strong> quando a IA escala a conversa; e{' '}
            <strong>plano de tratamento aceito ou recusado</strong> pelo portal
            do paciente, que inclui os dentistas. Só o dono recebe o aviso de{' '}
            <strong>assinatura em atraso</strong>. Cancelamento, falta e
            pagamento de paciente <em>não</em> geram aviso no sino.
          </>
        ),
        keywords:
          'quais notificacoes eventos agendamento remarcado confirmada escalonamento plano aceito assinatura atraso',
      },
      {
        q: 'Como marco as notificações como lidas — e como vou direto pro assunto?',
        a: (
          <>
            Clicar na notificação já a marca como lida e{' '}
            <strong>leva você direto ao item</strong>: avisos de agenda e de
            WhatsApp abrem a <Link to="/agenda">Agenda</Link>; os de plano de
            tratamento abrem o plano correspondente. Pra zerar tudo de uma vez,
            use <strong>Marcar todas como lidas</strong> no topo do painel.
          </>
        ),
        keywords:
          'marcar como lida todas lidas limpar notificacao clicar abrir ir para',
      },
      {
        q: 'Onde escolho por quais canais quero ser avisado?',
        a: (
          <>
            Em <Link to="/perfil">Meu perfil → Notificações</Link> existe uma
            grade de <strong>canal × evento</strong> (e-mail, app e WhatsApp
            para lembrete, confirmação, cancelamento, fatura a vencer, fatura
            paga, campanhas e avisos do sistema). Tudo vem ligado; você desmarca
            o que não quiser. Isso vale para os envios ao seu contato — o sino
            dentro do sistema continua mostrando tudo.
          </>
        ),
        keywords:
          'preferencia notificacao canal email whatsapp push desligar aviso receber',
      },
      {
        q: 'O que eu configuro na página Meu perfil?',
        a: (
          <>
            Em <Link to="/perfil">Meu perfil</Link> → aba{' '}
            <strong>Dados pessoais</strong>: foto (upload de até 2 MB ou
            importada do LinkedIn), <strong>assinatura para documentos</strong>{' '}
            (imagem usada em receitas e atestados), nome, nascimento, telefone e
            WhatsApp, LinkedIn, conselho/número/UF, especialidade,{' '}
            <strong>Bio (perfil público)</strong> e endereço (o CEP preenche
            rua, bairro e cidade sozinho). O e-mail e a função não são editáveis
            por aqui. Vão para o perfil público: foto, nome, bio,
            especialidade, CRO e LinkedIn — telefone, e-mail e endereço nunca
            aparecem.
          </>
        ),
        keywords:
          'meu perfil dados pessoais foto avatar assinatura bio telefone endereco cep editar',
      },
      {
        q: 'Onde troco minha senha e ativo o 2FA?',
        a: (
          <>
            Tudo em <Link to="/perfil">Meu perfil → Segurança</Link>: trocar
            senha, ativar a <strong>verificação em duas etapas (2FA)</strong>{' '}
            lendo o QR code num app autenticador (guarde os códigos de backup —
            eles aparecem uma única vez) e ver/encerrar suas{' '}
            <strong>sessões ativas</strong>. A <strong>política</strong> que
            <em> exige</em> 2FA de donos e gerentes é da clínica e fica em{' '}
            <Link to="/configuracoes">Configurações → Segurança</Link> (só o
            proprietário).
          </>
        ),
        keywords:
          'senha trocar 2fa dois fatores mfa autenticador sessoes ativas seguranca codigo backup',
      },
      {
        q: 'Com 2FA ativado, como faço login? E se eu perder o celular?',
        a: (
          <>
            Depois do e-mail e senha, o sistema pede o{' '}
            <strong>código de 6 dígitos</strong> do seu app autenticador
            (Google Authenticator, Authy, 1Password) — digite e entre. Se você
            estiver <strong>sem o celular</strong>, use um dos{' '}
            <strong>códigos de recuperação</strong> (formato{' '}
            <span className="font-mono">xxxx-xxxx</span>) que apareceram quando
            você ativou o 2FA — cada um vale uma vez. Sem o app e sem códigos, o{' '}
            <strong>dono ou gerente</strong> da clínica precisa resetar seu
            acesso.
          </>
        ),
        keywords:
          'login 2fa codigo verificacao verificação nao entra perdi celular telefone app autenticador codigo recuperacao recuperação backup travado bloqueado sem acesso',
      },
      {
        q: 'O sistema me desconectou sozinho — por quê?',
        a: (
          <>
            Por segurança, a sessão encerra sozinha depois de um período{' '}
            <strong>sem nenhuma atividade</strong> (mouse, teclado ou toque) —
            importante no computador compartilhado da recepção, pra ninguém
            entrar na conta de outra pessoa deixada aberta. O tempo é{' '}
            <strong>ajustável pelo proprietário</strong> em{' '}
            <Link to="/configuracoes">Configurações → Segurança → Tempo de
            sessão</Link> (padrão 15 minutos; de 5 min a 8 h). Ao voltar,
            aparece o aviso <em>"sessão encerrada por inatividade"</em> e é só
            entrar de novo. Enquanto você estiver usando o sistema, não
            desconecta; e trabalhar em qualquer aba mantém as demais ativas.
          </>
        ),
        keywords:
          'deslogado desconectado sozinho sessao expirou caiu inatividade tempo ocioso auto logout seguranca recepcao computador compartilhado 15 minutos ajustar aumentar tempo sessao configuracoes',
      },
    ],
  },

  // ───────────────────── ADMINISTRAÇÃO DA CLÍNICA ─────────────────────
  // Configuração, pessoas, assinatura, LGPD e integrações — território
  // de dono/gerente.
  {
    id: 'especialidades',
    icon: '🏥',
    title: 'Especialidades da clínica',
    audience: 'admin',
    items: [
      {
        q: 'Como defino as áreas de atuação da clínica?',
        a: (
          <>
            Em <strong>Configurações → Especialidades</strong> (somente
            dono/gerente), marque as áreas: odontologia, fisioterapia,
            estética, nutrição, psicologia etc. Isso define quais módulos
            clínicos aparecem no prontuário de TODOS os pacientes — e o
            backend bloqueia o que não estiver habilitado.
          </>
        ),
        keywords: 'especialidade área atuação módulos habilitar fisio',
      },
      {
        q: 'Minha clínica é multidisciplinar — como fica o prontuário?',
        a: (
          <>
            Marque todas as áreas que a clínica atende. O prontuário do
            paciente é único: cada profissional vê os módulos da própria
            área em destaque, e os registros das demais ficam acessíveis
            (visão integrada do paciente). Sigilo reforçado por área está no
            roadmap.
          </>
        ),
        keywords: 'multidisciplinar várias especialidades junto odonto estética',
      },
      {
        q: 'Cadastrei a clínica como odonto, posso mudar depois?',
        a: (
          <>
            Sim, a qualquer momento — a mudança vale na hora pra equipe
            inteira. Dados já registrados nunca somem: se desabilitar uma
            área, as fichas dela ficam preservadas e voltam a aparecer se
            reabilitar.
          </>
        ),
        keywords: 'mudar trocar especialidade depois desabilitar',
      },
      {
        q: 'Qual a diferença entre marcar as especialidades e a barra "Visão"?',
        a: (
          <>
            São dois controles diferentes. <strong>Configurações → Geral →
            Especialidades</strong> define, para a clínica inteira,{' '}
            <em>quais</em> módulos clínicos existem (odontograma,
            periodontograma, mapa corporal, escala de dor, avaliação estética,
            fotos antes/depois, antropometria) — é a única coisa que o backend
            bloqueia. Já a barra <strong>Visão</strong>, no topo do prontuário, é
            uma preferência de cada profissional: filtra o que ele vê, sem mexer
            no que a clínica oferece.
          </>
        ),
        keywords:
          'visao lente especialidade diferenca modulos clinica profissional filtro prontuario',
      },
      {
        q: 'Por que a barra "Visão" não aparece pra mim?',
        a: (
          <>
            Ela só existe quando a clínica tem <strong>mais de uma
            especialidade</strong> marcada — com uma só não haveria o que
            filtrar. A escolha fica guardada no navegador daquele computador ou
            tablet e vale tanto na aba Prontuário do paciente quanto na página{' '}
            <Link to="/prontuario">Prontuário</Link>. Se você nunca escolheu, o
            sistema usa o seu conselho como pista: CRO abre em Odontologia,
            CREFITO em Fisioterapia, CRN em Nutrição, CRP em Psicologia.
          </>
        ),
        keywords:
          'visao nao aparece lente uma especialidade conselho cro crefito crn crp padrao',
      },
    ],
  },
  {
    id: 'equipe',
    icon: '👥',
    title: 'Equipe e permissões',
    audience: 'admin',
    items: [
      {
        q: 'Quais são os papéis e o que cada um pode fazer?',
        a: (
          <>
            <strong>Dono</strong>: tudo, incluindo assinatura e exclusões.{' '}
            <strong>Gerente</strong>: mesmo acesso de gestão do dono —
            operação completa, configurações, papéis e assinatura (algumas
            integrações seguem exclusivas do dono).{' '}
            <strong>Profissional clínico</strong>: agenda e
            prontuário, com permissão de <em>escrever</em> no prontuário.{' '}
            <strong>Assistente</strong> e <strong>Recepção</strong>: agenda,
            cadastro e cobrança, e leitura do prontuário — mas sem escrever
            nele e sem o botão <strong>🩺 Atender agora</strong>. O acesso a
            Configurações, Integrações, Templates e Campanhas é só de dono e
            gerente.
          </>
        ),
        keywords:
          'papel permissão role acesso dono gerente recepção assistente rbac quem pode ver prontuario',
      },
      {
        q: 'Convidei alguém e a pessoa não consegue se cadastrar — o que faço?',
        a: (
          <>
            O convite chega por <strong>e-mail</strong> com um link para a
            pessoa <strong>criar a própria senha</strong> (mínimo 10 caracteres)
            e ativar o acesso. Quando o template de WhatsApp estiver aprovado, o
            mesmo link também é enviado por <strong>WhatsApp</strong> para o
            número informado no convite (em paralelo ao e-mail, não no lugar
            dele). O link <strong>expira em 7 dias</strong>. Se
            expirou ou o e-mail não chegou, vá em{' '}
            <Link to="/equipe">Equipe</Link>: no bloco{' '}
            <strong>Convites pendentes</strong> você vê o status (dias
            restantes / expirado) e pode <strong>Reenviar</strong> (gera um link
            novo e reinicia o prazo — o antigo deixa de valer) ou{' '}
            <strong>Cancelar</strong> o convite.
          </>
        ),
        keywords:
          'convite convidar membro equipe nao consegue cadastrar link expirou expirado reenviar cancelar 404 nao chegou email whatsapp zap pendente ativar conta senha',
      },
      {
        q: 'Um membro esqueceu a senha — como reseto?',
        a: (
          <>
            Em <Link to="/equipe">Equipe</Link>, na linha do membro, clique em{' '}
            <strong>Resetar senha</strong> (dono ou gerente). O sistema gera uma{' '}
            <strong>senha temporária</strong>, <strong>envia por e-mail</strong>{' '}
            para o membro e também mostra na tela (uma única vez) caso você
            prefira repassar direto. Isso invalida a senha antiga e desconecta o
            membro de todos os aparelhos. No próximo login ele usa a temporária e
            o sistema pede pra criar uma nova. Se perder, é só resetar de novo.
          </>
        ),
        keywords:
          'resetar senha esqueci senha membro equipe temporaria temporária primeiro login trocar senha dono gerente desconectar sessao',
      },
      {
        q: 'Como registro o conselho profissional (CRO, CREFITO, CRP…)?',
        a: (
          <>
            Em <Link to="/equipe">Equipe → Editar membro</Link>, seção
            “Dados profissionais”: escolha o conselho, número e UF. Aparece
            em documentos (receitas, atestados) e, no caso do{' '}
            <strong>CRO</strong>, também no perfil público do profissional —
            os demais conselhos ainda não são exibidos na página pública.
          </>
        ),
        keywords: 'cro crefito crp crn conselho registro profissional uf',
      },
      {
        q: 'Como defino comissão e meta de faturamento de um profissional?',
        a: (
          <>
            Em <Link to="/equipe">Equipe → Editar membro</Link> preencha{' '}
            <strong>Comissão (%)</strong> — percentual sobre a receita{' '}
            <strong>paga</strong> atribuída a ele — e{' '}
            <strong>Meta mensal (R$)</strong>. Valem para{' '}
            <em>qualquer papel</em> (inclusive estética e fisio), não só
            dentista. Deixe em branco para sem comissão ou sem meta. Os dois
            aparecem no relatório <strong>Recibos por profissional</strong>, e a
            meta também alimenta o <strong>ranking do painel de Metas</strong>,
            ambos em <Link to="/relatorios">Relatórios</Link>.
          </>
        ),
        keywords:
          'comissão comissao meta mensal faturamento percentual % profissional definir configurar quanto pagar recibos editar membro remuneração bonus',
      },
      {
        q: 'Posso criar um papel com permissões específicas (além dos padrões)?',
        a: (
          <>
            Sim. Em <strong>Configurações → Papéis e permissões</strong> (dono
            ou gerente), clique em <strong>Novo papel</strong>, dê um nome e
            marque as permissões agrupadas por módulo. Depois, em{' '}
            <Link to="/equipe">Equipe → Editar membro</Link>, atribua esse papel
            à pessoa. Os papéis-base (marcados como <strong>Sistema</strong>)
            não podem ser renomeados nem excluídos, mas você pode ajustar as
            permissões deles.
          </>
        ),
        keywords:
          'papel personalizado customizado criar permissão específica rbac novo papel sistema configurar acesso granular estética fisio recepção',
      },
      {
        q: 'Não consigo excluir um papel ou tirar uma permissão. Por quê?',
        a: (
          <>
            Duas travas de segurança: um papel <strong>não é excluído</strong>{' '}
            enquanto estiver atribuído a alguém — reatribua os membros a outro
            papel primeiro. E o sistema <strong>não deixa remover a última
            pessoa</strong> com permissão de <strong>Gerenciar
            configurações</strong>, pra ninguém ficar trancado pra fora da
            administração da clínica. Papéis-base <strong>Sistema</strong>{' '}
            também não podem ser apagados.
          </>
        ),
        keywords:
          'não consigo excluir papel remover permissão atribuído travado lockout última pessoa configurações bloqueado sistema reatribuir',
      },
      {
        q: 'Como limito um profissional a uma unidade?',
        a: (
          <>
            Com multi-unidade ativo, edite o membro e marque as unidades de
            acesso. A agenda e os pacientes exibidos respeitam o vínculo.
          </>
        ),
        keywords: 'unidade filial multi-unidade vincular acesso',
      },
      {
        q: 'Aparece que meu plano não permite adicionar outro profissional. O que é?',
        a: (
          <>
            Cada plano inclui um número de <strong>profissionais que
            atendem</strong> — os <strong>dentistas</strong> e o próprio{' '}
            <strong>dono</strong> contam; recepção, assistente e gerente{' '}
            <strong>não</strong> ocupam vaga. O <strong>Starter</strong> inclui
            1, o <strong>Pro</strong> até 8 e o <strong>Scale</strong> é
            ilimitado. Quem já está cadastrado continua funcionando — o aviso só
            aparece ao convidar ou cadastrar um profissional{' '}
            <strong>novo</strong> acima do limite. Para liberar mais, faça
            upgrade do plano.
          </>
        ),
        keywords:
          'plano nao permite limite profissional dentista nao consigo convidar adicionar cadastrar assento vaga bloqueado upgrade starter pro scale pagar faca upgrade',
      },
      {
        q: 'Não consigo cadastrar outra unidade (filial). Por quê?',
        a: (
          <>
            O número de unidades também segue o plano: <strong>Starter</strong>{' '}
            1, <strong>Pro</strong> 3 e <strong>Scale</strong> até 99. As
            unidades já cadastradas em{' '}
            <strong>Configurações → Unidades</strong> continuam valendo; o
            bloqueio só impede criar uma <strong>nova</strong> acima do limite.
            Para abrir mais filiais, faça upgrade.
          </>
        ),
        keywords:
          'unidade filial limite plano nao consigo cadastrar criar adicionar multi-unidade upgrade starter pro scale bloqueado',
      },
      {
        q: 'Profissional saiu da clínica — como removo?',
        a: (
          <>
            Em Equipe, mude o status para <strong>Inativo</strong>. O acesso
            é cortado na hora, mas o histórico clínico assinado por ele
            permanece íntegro (exigência legal). Nunca excluímos a pessoa do
            histórico.
          </>
        ),
        keywords: 'desativar remover demitir desligar usuário inativo',
      },
    ],
  },
  {
    id: 'config-admin',
    icon: '⚙️',
    title: 'Configurações essenciais',
    audience: 'admin',
    items: [
      {
        q: 'Como configuro horários de atendimento e feriados?',
        a: (
          <>
            <strong>Configurações → Horários</strong>: faixa de atendimento
            global, horário por dia da semana (ex.: sábado meio período) e
            feriados/folgas. Cada profissional ainda pode ter expediente
            próprio — edite em <strong>Equipe → ⋯ → Editar membro</strong>, no
            bloco <strong>Expediente (horário de trabalho)</strong>, ou cada um
            no seu “Meu expediente”. Quando o profissional tem horário próprio,
            a agenda, o booking público e o agente de IA só oferecem horários
            dentro desse expediente (e nada em dias de folga).
          </>
        ),
        keywords:
          'horário funcionamento feriado sábado expediente jornada profissional editar membro equipe horário próprio agente agenda',
      },
      {
        q: 'Como defino a política de cancelamento e no-show?',
        a: (
          <>
            <strong>Configurações → Política de cancelamento</strong>:
            antecedência mínima sem multa, valor de multa por falta e o
            texto exibido ao paciente nas confirmações.
          </>
        ),
        keywords: 'cancelamento multa no-show política antecedência',
      },
      {
        q: 'Posso usar minha marca (logo e cores)?',
        a: (
          <>
            Sim — <strong>Configurações → Marca</strong> (white-label): logo,
            cores e nome de exibição aplicados no app, no portal do paciente
            e nos e-mails.
          </>
        ),
        keywords: 'logo marca cor branding white-label personalizar',
      },
      {
        q: 'Como personalizo a numeração de faturas e agendamentos?',
        a: (
          <>
            São duas seções separadas:{' '}
            <strong>Configurações → Agenda → Numeração de agendamento</strong> e{' '}
            <strong>Configurações → Financeiro → Numeração de fatura</strong>.
            Em cada uma você define prefixo e quantidade de dígitos (ex.:
            NF-000123 ou 2026-0001). Pacientes citam esses códigos por telefone
            — mantenha curto.
          </>
        ),
        keywords: 'numeração código fatura agendamento prefixo sequência',
      },
      {
        q: 'Como desativo vários serviços de uma vez ou filtro o catálogo?',
        a: (
          <>
            No <Link to="/servicos">Catálogo de Serviços</Link>, marque a{' '}
            <strong>caixa</strong> de cada serviço (ou o checkbox do cabeçalho
            pra selecionar a página toda) e use a barra que aparece pra{' '}
            <strong>Desativar</strong> ou <strong>Reativar</strong> em massa —
            desativar é um arquivamento (não apaga histórico), dá pra reativar
            depois. Para achar o que precisa, abra{' '}
            <strong>Filtros avançados</strong>: faixa de{' '}
            <strong>preço</strong> e serviços <strong>com regra fiscal própria</strong>{' '}
            ou que <strong>herdam da clínica</strong>, além da busca por
            código/nome, área e status (ativos/inativos). Só o dono e gerentes
            alteram o catálogo.
          </>
        ),
        keywords:
          'catálogo serviços desativar ativar massa lote vários selecionar invalidar arquivar filtro avançado preço regra fiscal buscar inativar bulk',
      },
      {
        q: 'O que são as regras tributárias e por que eu preciso delas?',
        a: (
          <>
            Uma regra agrupa os códigos fiscais de um <strong>tipo de
            serviço</strong> (ex.: odontologia x estética): item da lista LC
            116, CNAE, código de tributação do município, alíquota de ISS,
            vigência e ISS retido. Cadastre em{' '}
            <strong>Configurações → Fiscal → Regras tributárias</strong> com{' '}
            <strong>+ Nova regra</strong> — assim, quando a alíquota mudar, você
            corrige num lugar só e todos os serviços seguem junto. Se já tem uma
            nota autorizada, use <strong>Importar XML da nota</strong> pra o
            sistema preencher os códigos pra você.
          </>
        ),
        keywords:
          'regra tributaria fiscal iss cnae lc116 codigo tributacao municipal nfse imposto aliquota',
      },
      {
        q: 'Como ligo uma regra tributária aos meus serviços?',
        a: (
          <>
            Dois caminhos. Um a um: <Link to="/servicos">Serviços</Link> →
            editar o serviço → campo <strong>Regra tributária (NFS-e)</strong>{' '}
            (deixar em “Herdar da clínica” usa o padrão de Integrações → NFS-e).
            Em lote: na própria seção de regras, botão{' '}
            <strong>Aplicar a serviços</strong>, marque os serviços e confirme.
            A tela avisa quantos serviços ativos ainda estão sem regra — vale
            zerar esse aviso antes de começar a emitir notas.
          </>
        ),
        keywords:
          'vincular regra servico aplicar a servicos herdar padrao fiscal nfse lote',
      },
      {
        q: 'Posso cadastrar várias regras tributárias de uma vez (planilha)?',
        a: (
          <>
            Pode. Em{' '}
            <strong>Configurações → Fiscal → Regras tributárias</strong>, use{' '}
            <strong>Baixar modelo</strong> pra pegar a planilha com as colunas
            certas (nome da regra, item da LC 116, CNAE, cTribNac, código
            municipal, IBGE e alíquota), preencha e volte em{' '}
            <strong>Importar regras (planilha)</strong>. Antes de gravar, o
            sistema mostra uma <strong>prévia</strong>: quantas regras vai criar,
            quantas vai atualizar e quais linhas têm erro. A chave é o{' '}
            <strong>nome da regra</strong> — reimportar a mesma planilha atualiza
            as regras existentes em vez de duplicar. O <strong>item da LC
            116</strong> é obrigatório em toda linha; o <strong>IBGE</strong>,
            quando preenchido, precisa ter 7 dígitos (Brasília = 5300108).
            Depois de importar, use <strong>Aplicar a serviços</strong> pra ligar
            cada regra aos serviços dela.
          </>
        ),
        keywords:
          'importar regras tributarias planilha xlsx csv massa lote modelo baixar previa nome regra lc116 ibge aliquota nfse fiscal em massa',
      },
    ],
  },
  {
    id: 'consentimento',
    icon: '✍️',
    title: 'Consentimento e políticas',
    audience: 'admin',
    items: [
      {
        q: 'Para que serve a tela Consentimento?',
        a: (
          <>
            Em <Link to="/consentimento">Consentimento</Link> ficam as{' '}
            <strong>políticas</strong> da clínica (o texto que o paciente
            aceita) e o <strong>histórico por paciente</strong>. Escolha o
            paciente na lista para ver tudo o que ele já concedeu ou revogou,
            com a política e a data. Criar política é exclusivo do dono; o
            resto da equipe consulta e registra consentimentos.
          </>
        ),
        keywords:
          'consentimento política politica lgpd onde fica tela dono permissão permissao histórico historico',
      },
      {
        q: 'Como sei quantos pacientes autorizaram o WhatsApp?',
        a: (
          <>
            No topo de <Link to="/consentimento">Consentimento</Link> há o card{' '}
            <strong>Cobertura de WhatsApp (opt-in)</strong>: mostra o{' '}
            <strong>percentual</strong> de quem autorizou e a quebra em{' '}
            <strong>autorizaram</strong>, <strong>sem resposta</strong>,{' '}
            <strong>revogaram</strong> e <strong>sem telefone</strong>. Só quem
            tem telefone <em>e</em> opt-in recebe lembrete e automação — por
            isso vale acompanhar esse número antes de ligar disparos em massa.
          </>
        ),
        keywords:
          'cobertura opt-in optin whatsapp quantos autorizaram percentual base pacientes lembrete automação disparo massa contatável',
      },
      {
        q: 'Que tipos de política existem e o que eu preencho?',
        a: (
          <>
            São cinco tipos fixos: <strong>Termos de uso</strong>,{' '}
            <strong>Privacidade</strong>, <strong>Marketing</strong>,{' '}
            <strong>WhatsApp</strong> e <strong>Uso de imagem</strong>. Em{' '}
            <strong>Nova política</strong> você escolhe o tipo e preenche a{' '}
            <strong>versão</strong> (texto livre, ex.: “1.0”), o{' '}
            <strong>conteúdo em markdown</strong> e a data em que ela passa a
            valer (<strong>Vigente a partir de</strong>). Não existe campo de
            título: na tabela a política é identificada pelo tipo, com Versão e{' '}
            <strong>Vigente desde</strong> ao lado.
          </>
        ),
        keywords:
          'tipo de política politica termos de uso privacidade marketing whatsapp uso de imagem versão versao conteudo markdown vigencia nova política titulo',
      },
      {
        q: 'Onde registro o termo de depilação a laser ou de procedimento estético?',
        a: (
          <>
            <strong>Não é nesta tela.</strong> A tela Consentimento guarda as
            políticas da clínica inteira (privacidade, WhatsApp, uso de imagem) —
            os cinco tipos fixos, e nenhum deles é de procedimento. Termo que o
            paciente assina antes de uma sessão vive{' '}
            <strong>no prontuário dele</strong>.
            <br />
            <br />
            O caminho: <Link to="/prontuario">Prontuário</Link> → selecione o
            paciente → aba <strong>🔏 Consentimentos</strong> →{' '}
            <strong>+ Novo consentimento</strong>. No campo{' '}
            <strong>Tipo</strong> estão <strong>✨ Depilação a laser</strong> e{' '}
            <strong>💆 Procedimento estético</strong>; escolher um deles{' '}
            <strong>já traz o texto do termo pronto</strong>, com anamnese,
            orientações pré e pós e as reações esperadas. Leia com o paciente,
            ajuste o que for específico do caso e registre.
            <br />
            <br />
            <strong>Cuidado ao trocar o Tipo:</strong> ele reescreve o campo do
            termo e <strong>apaga o que você tinha editado</strong>, sem
            perguntar. Escolha o tipo primeiro, edite depois.
            <br />
            <br />
            A assinatura é no painel, com o dedo ou a caneta. Se o paciente
            assinou em papel, marque <strong>📝 Papel (sem captura)</strong> —
            aí o sistema não exige o desenho.
          </>
        ),
        keywords:
          'depilacao depilação laser termo estetico estética procedimento consentimento assinatura prontuario aba consentimentos onde registro sessao',
      },
      {
        q: 'Publiquei uma versão nova — o que acontece com a anterior?',
        a: (
          <>
            Ela continua lá. Não existe status “ativa” ou “arquivada”: quem
            manda é a <strong>data de vigência</strong> — passa a valer a versão
            mais recente já em vigor, e as anteriores seguem listadas. Os
            consentimentos antigos continuam apontando para a{' '}
            <strong>versão exata</strong> que aquele paciente aceitou, e é isso
            que sustenta a prova se alguém questionar depois.
          </>
        ),
        keywords:
          'versao nova publicar substituir vigencia vigente desde status ativa arquivada historico prova',
      },
      {
        q: 'Como registro a concessão ou a revogação de um paciente?',
        a: (
          <>
            Selecione o paciente e clique em{' '}
            <strong>Registrar consentimento</strong>: escolha a política (o
            seletor mostra tipo e versão), marque a ação{' '}
            <strong>Concedido</strong> ou <strong>Revogado</strong> e confirme —
            são só esses dois campos. Na concessão aparece o painel de
            assinatura, obrigatório: o paciente assina com o dedo ou o mouse e a
            assinatura fica guardada como evidência junto com a data. A
            revogação não pede assinatura, porque retirar o consentimento é um
            direito que o titular exerce sozinho.
          </>
        ),
        keywords:
          'registrar consentimento conceder revogar revogação revogacao assinatura obrigatoria paciente evidência evidencia notas',
      },
      {
        q: 'Onde o paciente assina?',
        a: (
          <>
            Presencialmente, com alguém da equipe logado: pela tela{' '}
            <Link to="/consentimento">Consentimento</Link> ou pela aba{' '}
            <strong>Consentimentos</strong> do prontuário, que já traz
            textos-modelo por tipo (tratamento odontológico, uso de imagens,
            convênio, cirurgia, anestesia, compartilhamento de dados) e aceita o
            modo <strong>papel</strong>, para quando a assinatura foi colhida
            impressa. No <strong>portal do paciente</strong> ele consulta o que
            já foi concedido ou revogado, pode <strong>revogar</strong> um
            consentimento e pode <strong>autorizar o WhatsApp</strong> (opt-in)
            sozinho — mas o portal não coleta assinatura de termos clínicos.
          </>
        ),
        keywords:
          'portal do paciente assinar assinatura onde presencial tablet papel prontuário prontuario termo autorizar whatsapp opt-in sozinho revogar',
      },
      {
        q: 'O paciente pode autorizar o WhatsApp sozinho?',
        a: (
          <>
            Sim. No <strong>portal do paciente</strong> (link pessoal, sem
            senha), quem ainda não autorizou vê o convite{' '}
            <strong>“Receba lembretes no WhatsApp”</strong> e toca em{' '}
            <strong>Autorizar WhatsApp</strong> — o opt-in é registrado na hora
            e ele passa a receber lembretes e automações. Pode cancelar pelo
            mesmo portal quando quiser. Acompanhe o total em{' '}
            <Link to="/consentimento">Consentimento → Cobertura de WhatsApp</Link>.
          </>
        ),
        keywords:
          'paciente autorizar whatsapp sozinho portal opt-in optin link receber lembretes conceder consentimento autorização',
      },
      {
        q: 'Dá pra apagar ou editar uma política ou um consentimento?',
        a: (
          <>
            Não, e isso é proposital: políticas e registros de consentimento são
            gravados em modo somente-acréscimo, com bloqueio no próprio banco de
            dados. Para parar de usar um texto, publique uma versão nova — a
            antiga fica no histórico como prova do que valia na época. Para
            desfazer um consentimento, registre uma <strong>revogação</strong>:
            ela entra como um novo evento e passa a valer dali em diante.
          </>
        ),
        keywords:
          'apagar excluir editar arquivar política politica imutável imutavel somente acréscimo acrescimo histórico',
      },
      {
        q: 'O que isso muda na prática, além da LGPD no papel?',
        a: (
          <>
            Cada registro guarda a base legal e o canal em que o consentimento
            foi coletado, montando a trilha que a LGPD espera da clínica. O
            efeito mais concreto no dia a dia é o WhatsApp: sem o consentimento
            de WhatsApp concedido (botão <strong>Autorizar</strong> no card
            Comunicação da ficha do paciente), o sistema não envia lembrete nem
            pesquisa de satisfação para ele. Pedidos do titular — acesso,
            exclusão, portabilidade — são tratados à parte, em{' '}
            <strong>Configurações → LGPD → Pedidos LGPD</strong>.
          </>
        ),
        keywords:
          'lgpd base legal anpd titular dsr pedidos lgpd whatsapp opt-in autorizar lembrete',
      },
    ],
  },
  {
    id: 'assinatura',
    icon: '💳',
    title: 'Assinatura e planos',
    audience: 'admin',
    items: [
      {
        q: 'Como funciona o teste grátis e o upgrade?',
        a: (
          <>
            14 dias com tudo liberado, sem cartão. Pra continuar, escolha o
            plano em <strong>Configurações → Assinatura</strong> e informe o
            CNPJ pra cobrança. Upgrade/downgrade valem imediatamente, sem
            fidelidade.
          </>
        ),
        keywords: 'trial teste plano upgrade assinatura pagar mensalidade',
      },
      {
        q: 'O que é o programa Founding 10?',
        a: (
          <>
            As 10 primeiras clínicas assinantes ganham desconto vitalício em
            troca de feedback e de toparem ser referência. O desconto fica
            registrado na assinatura e aparece como badge no painel.
          </>
        ),
        keywords: 'founding fundador desconto vitalício programa',
      },
      {
        q: 'A IA tem limite de uso?',
        a: (
          <>
            Sim: no plano <strong>Pro</strong> são 200 usos de IA por mês
            (transcrição, SOAP, anamnese); no <strong>Scale</strong>, 10.000 por
            mês, e a análise de radiografias é exclusiva do Scale. Como a chave
            é da sua própria conta OpenAI (<Link to="/integracoes">Integrações
            → OpenAI</Link>), o custo por análise você acompanha direto no painel
            da OpenAI.
          </>
        ),
        keywords: 'ia limite consumo custo chamadas pro scale openai',
      },
    ],
  },
  {
    id: 'integracoes',
    icon: '🔌',
    title: 'Integrações',
    audience: 'admin',
    items: [
      {
        q: 'Como ativo cobrança online (PIX/boleto/cartão)?',
        a: (
          <>
            Em <Link to="/integracoes">Integrações</Link>, conecte sua conta
            Asaas (você pode usar a própria — BYOK — e o dinheiro cai direto
            na sua conta). Depois disso o botão “Gerar link de pagamento”
            aparece nas faturas.
          </>
        ),
        keywords: 'asaas pix boleto cartão gateway pagamento online byok',
      },
      {
        q: 'Como conecto o WhatsApp oficial?',
        a: (
          <>
            Em Integrações → WhatsApp Business: conecte o número e aprove os
            templates. Sem a integração, os envios abrem o WhatsApp Web com
            a mensagem pronta (modo manual).
          </>
        ),
        keywords: 'whatsapp business api oficial número conectar',
      },
      {
        q: 'Como faço o sistema enviar e-mails com o endereço da clínica?',
        a: (
          <>
            Em <Link to="/integracoes">Integrações → E-mail da clínica</Link>,
            escolha o provedor (<strong>Google Workspace/Gmail</strong>,{' '}
            <strong>Microsoft 365/Outlook</strong> ou SMTP personalizado),
            informe o remetente, o usuário e uma <strong>senha de app</strong>{' '}
            (não a senha normal da caixa), marque “Usar este e-mail para os
            envios da clínica” e use <strong>Enviar teste</strong> pra
            confirmar. Sem isso, os e-mails saem por um remetente genérico do
            sistema.
          </>
        ),
        keywords:
          'email smtp gmail outlook remetente senha de app enviar teste correio configurar',
      },
      {
        q: 'Quais e-mails saem pelo endereço da clínica?',
        a: (
          <>
            Hoje dois: o <strong>convite para novos membros da equipe</strong> e
            o aviso de <strong>NFS-e emitida</strong> enviado ao paciente. Os
            demais (recuperação de senha, avisos da sua assinatura) saem pelo
            remetente do sistema de propósito, pra continuarem funcionando mesmo
            se o SMTP da clínica cair.
          </>
        ),
        keywords: 'quais emails convite equipe nfse paciente remetente sistema',
      },
      {
        q: 'Como configuro a emissão de nota fiscal (NFS-e)?',
        a: (
          <>
            Primeiro complete <strong>CNPJ, inscrição municipal e código do
            município</strong> em <strong>Configurações → Geral →
            Informações</strong>. Depois, em{' '}
            <Link to="/integracoes">Integrações → NFS-e</Link>, escolha o
            provedor <strong>Focus NFe</strong>, o ambiente (Homologação pra
            testar, Produção pra valer), cole o token e preencha item da lista
            (LC 116), CNAE, código de tributação municipal, alíquota de ISS e a
            série/próximo número do RPS. Só com tudo isso o botão “Emitir NFS-e
            por esta clínica” libera.
          </>
        ),
        keywords:
          'nfse nota fiscal focus nfe token homologacao producao cnpj inscricao municipal iss rps configurar',
      },
      {
        q: 'Dá pra emitir a nota automaticamente quando a fatura é paga?',
        a: (
          <>
            Sim: em <strong>Configurações → Fiscal → Regras tributárias</strong>,
            ligue <strong>“Emitir automaticamente quando a fatura for
            paga”</strong>. O sistema passa a emitir sozinho as faturas pagas
            dali pra frente (não emite retroativo) e tenta uma vez por fatura —
            se a prefeitura recusar, a fatura mostra o erro pra você corrigir e
            emitir manualmente.
          </>
        ),
        keywords:
          'nfse automatica emitir sozinho fatura paga automacao regras tributarias',
      },
      {
        q: 'A agenda sincroniza com Google Calendar?',
        a: (
          <>
            Sim: cada profissional conecta a própria conta em{' '}
            <strong>Configurações → Integrações → Google Calendar</strong>{' '}
            (é lá, e não na página Integrações do menu). Quem só quer ver a
            agenda em outro calendário pode assinar o feed somente-leitura em{' '}
            <strong>Configurações → Integrações → iCal pessoal</strong>.
          </>
        ),
        keywords: 'google calendar ical sincronizar agenda externa feed',
      },
      {
        q: 'Como ativo a IA (análise de radiografias e voz)?',
        a: (
          <>
            Em <Link to="/integracoes">Integrações → OpenAI</Link>, cole a
            chave da SUA conta OpenAI (modelo BYOK — o custo de IA é pago
            por você direto à OpenAI, centavos por imagem). Com a chave
            ativa, aparecem: <strong>Analisar IA</strong> nos arquivos de
            imagem, <strong>sugestões da IA no odontograma</strong> e o{' '}
            <strong>ditado por voz</strong> no Modo Cadeira. Disponível no
            plano Scale.
          </>
        ),
        keywords: 'openai ia inteligencia artificial chave api byok ativar raio-x voz',
      },
      {
        q: 'Qual modelo de IA escolher (gpt-4o-mini ou gpt-4o)?',
        a: (
          <>
            É uma escolha de custo × precisão, feita no próprio card OpenAI
            (campo “modelo de visão”), sem depender do suporte:
            <br />• <strong>gpt-4o-mini</strong> — triagem econômica.
            Aponta achados evidentes (restaurações grandes, dentes
            ausentes), mas pode errar a numeração do dente e perder
            estruturas sutis. Custo por imagem ~10x menor.
            <br />• <strong>gpt-4o</strong> — recomendado para uso clínico.
            Detecta mais (germes de siso, lesões pequenas), localiza melhor
            e descreve em linguagem anatômica. Custa mais por análise,
            ainda na casa de centavos.
            <br />
            Em ambos os casos a IA é <strong>auxiliar de triagem</strong>:
            ela aponta onde olhar, e cada achado exige o “Li e revisei” do
            profissional antes de entrar no prontuário. Não substitui
            laudo radiológico.
          </>
        ),
        keywords: 'modelo gpt-4o mini visao escolher custo precisao radiografia analise',
      },
      {
        q: 'Tenho sistema próprio — existem webhooks/API?',
        a: (
          <>
            Sim (plano Scale): webhooks de eventos (consulta criada, fatura
            paga…) em <strong>Configurações → Integrações → Webhooks</strong>,
            com assinatura secreta por endpoint — o painel aparece{' '}
            <strong>somente para o dono</strong> da conta. API documentada sob
            demanda: fale com o <Link to="/suporte">Suporte</Link>.
          </>
        ),
        keywords: 'webhook api integração evento desenvolvedor erp dono',
      },
    ],
  },
  {
    id: 'seguranca',
    icon: '🔒',
    title: 'Segurança e LGPD',
    audience: 'admin',
    items: [
      {
        q: 'Como ativo o duplo fator (2FA) pra equipe?',
        a: (
          <>
            Cada pessoa ativa o seu em{' '}
            <Link to="/perfil">Meu perfil → Segurança</Link> (app autenticador,
            com códigos de backup mostrados uma única vez). Para tornar
            obrigatório, o <strong>proprietário</strong> vai em{' '}
            <Link to="/configuracoes">Configurações → Segurança</Link> e marca{' '}
            <strong>Exigir 2FA para proprietários e gerentes</strong>, com{' '}
            <strong>prazo de configuração</strong> em dias. Atenção: o sistema só
            deixa exigir quando <strong>todos</strong> os donos e gerentes já
            configuraram o 2FA — senão eles ficariam trancados fora. A tela
            mostra quem ainda falta.
          </>
        ),
        keywords: '2fa mfa autenticação dois fatores obrigatório segurança perfil configurações política exigir travado bloqueado',
      },
      {
        q: 'O que fica registrado na auditoria?',
        a: (
          <>
            Toda ação relevante: quem fez, quando, o quê e de onde —
            imutável. Isso inclui <strong>criar/editar/excluir</strong> qualquer
            dado, <strong>logins</strong> (sucesso e tentativas falhas, com
            IP), <strong>acesso a prontuário</strong> (quem abriu a ficha de
            qual paciente) e <strong>exportações</strong> (quem baixou base de
            pacientes, receita ou agenda). Consulte em{' '}
            <strong>Configurações → LGPD → Trilha de auditoria</strong> com
            filtros por usuário, ação e período, e exporte em CSV. É a sua
            evidência em disputa trabalhista, incidente de segurança ou
            fiscalização.
          </>
        ),
        keywords: 'auditoria log histórico quem alterou rastreabilidade trilha login acesso prontuário exportação ip tentativa falha segurança lgpd quem abriu ficha',
      },
      {
        q: 'Paciente pediu os dados dele (ou exclusão) — e agora?',
        a: (
          <>
            Use <strong>Configurações → LGPD → Pedidos LGPD</strong>:
            exportação completa dos dados do titular em um clique e
            anonimização/exclusão respeitando a retenção legal do prontuário.
            Cada pedido atendido fica registrado na trilha de auditoria.
          </>
        ),
        keywords: 'dsr lgpd titular exclusão acesso dados anonimizar pedido',
      },
      {
        q: 'Por quanto tempo os dados ficam guardados?',
        a: (
          <>
            Você controla em <strong>Configurações → Retenção de
            dados</strong> (com mínimos legais de prontuário respeitados).
            Backups são diários e automáticos, com restauração testada.
          </>
        ),
        keywords: 'retenção backup guardar prazo apagar política',
      },
      {
        q: 'Quem consegue ver os dados da minha clínica?',
        a: (
          <>
            Só a sua equipe. O isolamento é por clínica no próprio banco
            (RLS multi-tenant) — outra clínica não enxerga nada seu, nem por
            falha de aplicação. Prontuário e CPF são criptografados em
            repouso.
          </>
        ),
        keywords: 'isolamento privacidade outra clínica criptografia tenant',
      },
    ],
  },

  // ───────────────────────── QUANDO EMPACAR ─────────────────────────
  {
    id: 'suporte',
    icon: '🆘',
    title: 'Suporte',
    audience: 'both',
    items: [
      {
        q: 'Como abro um chamado?',
        a: (
          <>
            Em <Link to="/suporte">Suporte → Novo chamado</Link>: escolha a
            categoria (bug, dúvida, sugestão, financeiro), descreva e anexe
            prints se ajudar. Você acompanha o status (aberto → em andamento
            → resolvido) na mesma tela.
          </>
        ),
        keywords: 'chamado ticket suporte problema bug ajuda contato',
      },
      {
        q: 'Qual o prazo de resposta?',
        a: (
          <>
            Dúvidas e bugs: até 1 dia útil (prioritário no plano Pro+).
            Urgências que travam a operação da clínica: marque prioridade{' '}
            <strong>Urgente</strong> no chamado.
          </>
        ),
        keywords: 'prazo sla resposta urgente demora',
      },
    ],
  },
];
